import { BRONZE_DATA, GOLD_DATA, SILVER_DATA } from '@/shared/api/demo/data/icebergData';

import type {
  AirflowDagDetailResponse,
  AirflowDagGridRun,
  AirflowDagGridRunsResponse,
  AirflowDagListItem,
  AirflowDagListResponse,
  AirflowDagListRunStatus,
  AirflowDagPatchBody,
  AirflowDagRunListItem,
  AirflowDagRunListResponse,
  AirflowDagRunState,
  AirflowDagStructureResponse,
  AirflowDagTask,
  AirflowDagTaskInstanceSummary,
  AirflowDagTaskInstanceSummaryResponse,
  AirflowDagTasksResponse,
  AirflowDagVersion,
  AirflowDagVersionSnapshot,
  AirflowLatestRunResponse,
  AirflowTaskInstance,
  AirflowTaskInstancesResponse,
  AirflowTaskInstanceState,
  AirflowTaskTimeDelta,
  DagCodeVersion,
  DagDashboardDetailRequest,
  DagDashboardListParams,
  DagDashboardListResponse,
  DagDashboardResponse,
  DagExecutionChartResponse,
  DagExecutionTaskInstance,
  DagImmutableCodeResponse,
  DagInstalledLibrary,
  DagListRunStatus,
  DagMaturity,
  DagOverviewResponse,
  DagTaskGraphResponse,
  DagTaskListResponse,
  DagTaskLogRequest,
  DagTaskLogResponse,
  DagTaskResponse,
  DagTemplateCodeRequest,
  DagTemplateCodeResponse,
  DagVersionCodeRequest,
  DagVersionCodeResponse,
  DagVersionListRequest,
  DagVersionListResponse,
  DagVersionResponse,
  DagVersionStatusItem,
  DeployDagResponse,
  GlobalLibraryListResponse,
  LibraryItem,
  SaveDagVersionResponse,
  ToggleDagBookmarkResponse,
  ToggleDagSchedulingRequest,
  TriggerDagRequest,
  TriggerDagResponse,
  UpdateDagRequest,
  UpdateDagResponse,
  UploadLibraryRequest,
  UploadLibraryResponse,
} from '@/entities/dag/domain/types';
import { DAG_MATURITY_TYPES } from '@/entities/dag/domain/types';
import { normalizeDagTaskStatus } from '@/entities/dag/lib/normalizeDagTaskStatus';
import { normalizeVersionLabel } from '@/entities/dag/lib/normalizeVersionLabel';

type DagMockRunStateMatrix = Record<string, AirflowTaskInstanceState>;

type DagMockRunDefinition = {
  duration: number;
  logicalDate: string;
  runId: string;
  runType: string;
  startDate: string;
  state: AirflowDagRunState;
  triggeredBy: AirflowDagRunListItem['triggered_by'];
  triggeringUserName: string | null;
  taskStates: DagMockRunStateMatrix;
};

type DagMockDetailFixture = {
  dagDetail: AirflowDagDetailResponse;
  dagCodeVersions: DagCodeVersion[];
  dagRuns: AirflowDagRunListItem[];
  dagTasks: AirflowDagTask[];
  latestTaskInstances: Record<string, AirflowTaskInstance>;
  latestRun: AirflowLatestRunResponse;
  runStateMatrix: Record<string, DagMockRunStateMatrix>;
  structure: AirflowDagStructureResponse;
};

type DagTaskSeed = {
  downstreamTaskIds: string[];
  operatorName: string;
  taskId: string;
  triggerRule: string;
};

type CreateAirflowDagInMockStoreParams = {
  name: string;
  maturity: DagMaturity;
  datasourceId: string;
  description: string;
  entityType?: string;
  tags: string[];
};

const MOCK_TIME_DELTA: AirflowTaskTimeDelta = {
  __type: 'TimeDelta',
  days: 0,
  seconds: 300,
  microseconds: 0,
};

const DAG_RUNTIME_PENDING_MS = 15_000;
const DAG_RUNTIME_SUCCESS_MS = 120_000;

type DagRuntimePhase = 'pending' | 'running' | 'success';

type DagRuntimeRunMeta = {
  runId: string;
  startedAt: string;
  version: number;
};

const DAG_VERSION_FIXTURE = (dagId: string, dagDisplayName: string, bundleVersion: string): AirflowDagVersion => ({
  id: `${dagId}-version-${bundleVersion}`,
  version_number: Number(bundleVersion.replace(/\D/g, '')) || 1,
  dag_id: dagId,
  bundle_name: `${dagId}-bundle`,
  bundle_version: bundleVersion,
  created_at: '2026-03-24T00:00:00Z',
  dag_display_name: dagDisplayName,
  bundle_url: `https://example.com/${dagId}/${bundleVersion}`,
});

type DagRunPattern = {
  duration: number;
  runType: string;
  state: AirflowDagRunState;
  triggeredBy: AirflowDagRunListItem['triggered_by'];
  triggeringUserName: string | null;
  taskStates: DagMockRunStateMatrix;
};

type DagFixtureDefinition = {
  dagId: string;
  dagDisplayName: string;
  maturity: DagMaturity;
  description: string;
  timetableSummary: string;
  owners: string[];
  tags: string[];
  versionHistory: string[];
  isPaused: boolean;
  latestRunStatus: DagListRunStatus;
  lastRunAt?: string;
  nextDagrunLogicalDate?: string;
  hasImportErrors: boolean;
  outputTables: string[];
  upstreamDatasetIds?: string[];
  previousVersionDescriptions: string[];
};

type ResolvedDagFixtureDefinition = DagFixtureDefinition &
  Required<Pick<DagFixtureDefinition, 'lastRunAt' | 'nextDagrunLogicalDate'>>;

const createDagTaskSeed = ({
  taskId,
  operatorName,
  triggerRule,
  downstreamTaskIds = [],
}: {
  taskId: string;
  operatorName: string;
  triggerRule: string;
  downstreamTaskIds?: string[];
}): DagTaskSeed => ({
  taskId,
  operatorName,
  triggerRule,
  downstreamTaskIds,
});

const getRunTaskIdByMaturity = (maturity: DagMaturity) => `run_${maturity}_etl`;

const createSingleDagTaskSeed = (maturity: DagMaturity): DagTaskSeed =>
  createDagTaskSeed({
    taskId: getRunTaskIdByMaturity(maturity),
    operatorName: 'PythonOperator',
    triggerRule: 'all_success',
  });

const DEFAULT_TASK_SEEDS_BY_MATURITY: Record<DagMaturity, DagTaskSeed[]> = {
  bronze: [createSingleDagTaskSeed(DAG_MATURITY_TYPES.BRONZE)],
  silver: [createSingleDagTaskSeed(DAG_MATURITY_TYPES.SILVER)],
  gold: [createSingleDagTaskSeed(DAG_MATURITY_TYPES.GOLD)],
};

const DAG_TASK_PROFILE_MAP = {
  bronze_gsp_ecms1_waveform_ingest: [
    createDagTaskSeed({
      taskId: 'extract_opcua',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['validate_payload'],
    }),
    createDagTaskSeed({
      taskId: 'validate_payload',
      operatorName: 'GreatExpectationsOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['split_chart_trend'],
    }),
    createDagTaskSeed({
      taskId: 'split_chart_trend',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['write_chart', 'write_trend', 'write_bearing_master'],
    }),
    createDagTaskSeed({
      taskId: 'write_chart',
      operatorName: 'PythonOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'write_trend',
      operatorName: 'PythonOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'write_bearing_master',
      operatorName: 'PythonOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  bronze_kmp_waveform_ingest: [
    createDagTaskSeed({
      taskId: 'read_influxdb',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['resample_waveform'],
    }),
    createDagTaskSeed({
      taskId: 'resample_waveform',
      operatorName: 'SparkSubmitOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['compute_trend'],
    }),
    createDagTaskSeed({
      taskId: 'compute_trend',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['write_bronze'],
    }),
    createDagTaskSeed({
      taskId: 'write_bronze',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  bronze_hanwha_waveform_ingest: [
    createDagTaskSeed({
      taskId: 'request_waveform_api',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['normalize_response', 'refresh_access_token'],
    }),
    createDagTaskSeed({
      taskId: 'normalize_response',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_chart', 'publish_trend', 'publish_bearing_master'],
    }),
    createDagTaskSeed({
      taskId: 'publish_chart',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'publish_trend',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'publish_bearing_master',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({ taskId: 'refresh_access_token', operatorName: 'PythonOperator', triggerRule: 'all_done' }),
  ],
  silver_gsp_ecms1_curation: [
    createDagTaskSeed({
      taskId: 'load_bronze',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['standardize_waveform'],
    }),
    createDagTaskSeed({
      taskId: 'standardize_waveform',
      operatorName: 'SparkSubmitOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['validate_quality'],
    }),
    createDagTaskSeed({
      taskId: 'validate_quality',
      operatorName: 'GreatExpectationsOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['join_bearing_master'],
    }),
    createDagTaskSeed({
      taskId: 'join_bearing_master',
      operatorName: 'SparkSubmitOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_waveform_preprocessed', 'publish_config'],
    }),
    createDagTaskSeed({
      taskId: 'publish_waveform_preprocessed',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'publish_config',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  silver_kmp_ecms_curation: [
    createDagTaskSeed({
      taskId: 'load_bronze',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['denoise_waveform'],
    }),
    createDagTaskSeed({
      taskId: 'denoise_waveform',
      operatorName: 'SparkSubmitOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['enrich_config'],
    }),
    createDagTaskSeed({
      taskId: 'enrich_config',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_silver'],
    }),
    createDagTaskSeed({
      taskId: 'publish_silver',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  silver_hanwha_impact_curation: [
    createDagTaskSeed({
      taskId: 'load_bronze',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['reconcile_schema'],
    }),
    createDagTaskSeed({
      taskId: 'reconcile_schema',
      operatorName: 'SparkSubmitOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['validate_quality'],
    }),
    createDagTaskSeed({
      taskId: 'validate_quality',
      operatorName: 'GreatExpectationsOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_waveform_preprocessed', 'publish_config'],
    }),
    createDagTaskSeed({
      taskId: 'publish_waveform_preprocessed',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'publish_config',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  gold_gsp_ecms1_feature_publish: [
    createDagTaskSeed({
      taskId: 'load_silver',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['compute_feature_window'],
    }),
    createDagTaskSeed({
      taskId: 'compute_feature_window',
      operatorName: 'PythonVirtualenvOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_feature', 'run_diagnosis'],
    }),
    createDagTaskSeed({
      taskId: 'publish_feature',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
    createDagTaskSeed({
      taskId: 'run_diagnosis',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_diagnosis'],
    }),
    createDagTaskSeed({
      taskId: 'publish_diagnosis',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  gold_kmp_ecms_feature_publish: [
    createDagTaskSeed({
      taskId: 'load_silver',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['aggregate_feature_vectors'],
    }),
    createDagTaskSeed({
      taskId: 'aggregate_feature_vectors',
      operatorName: 'PythonVirtualenvOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['run_diagnosis'],
    }),
    createDagTaskSeed({
      taskId: 'run_diagnosis',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_feature'],
    }),
    createDagTaskSeed({
      taskId: 'publish_feature',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
      downstreamTaskIds: ['publish_diagnosis'],
    }),
    createDagTaskSeed({
      taskId: 'publish_diagnosis',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
  gold_hanwha_impact_feature_publish: [
    createDagTaskSeed({
      taskId: 'load_silver',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['compute_feature_pack'],
    }),
    createDagTaskSeed({
      taskId: 'compute_feature_pack',
      operatorName: 'PythonVirtualenvOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['run_diagnosis'],
    }),
    createDagTaskSeed({
      taskId: 'run_diagnosis',
      operatorName: 'PythonOperator',
      triggerRule: 'all_success',
      downstreamTaskIds: ['publish_gold'],
    }),
    createDagTaskSeed({
      taskId: 'publish_gold',
      operatorName: 'BashOperator',
      triggerRule: 'none_failed_min_one_success',
    }),
  ],
} satisfies Record<string, DagTaskSeed[]>;

const TABLE_NAMES_BY_MATURITY: Record<DagMaturity, Set<string>> = {
  bronze: new Set(BRONZE_DATA.map((hit) => hit._source.name)),
  silver: new Set(SILVER_DATA.map((hit) => hit._source.name)),
  gold: new Set(GOLD_DATA.map((hit) => hit._source.name)),
};

const INITIAL_DAG_RUN_COUNT = 20;
const MINUTE_IN_MILLISECONDS = 60 * 1000;
const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS;
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const getDagMaturityFromTagNames = (tags: string[]): DagMaturity => {
  const normalizedTags = tags.map((tag) => tag.trim().toLowerCase());

  if (normalizedTags.includes(DAG_MATURITY_TYPES.GOLD)) return DAG_MATURITY_TYPES.GOLD;
  if (normalizedTags.includes(DAG_MATURITY_TYPES.SILVER)) return DAG_MATURITY_TYPES.SILVER;
  return DAG_MATURITY_TYPES.BRONZE;
};

const getDagMaturity = (dag: AirflowDagListItem): DagMaturity => {
  const latestSnapshotTags = dag.version_snapshots?.[0]?.tags ?? [];
  const dagTags = (dag.tags ?? []).map((tag) => tag.name);

  return getDagMaturityFromTagNames([...latestSnapshotTags, ...dagTags]);
};

const getTaskSeedsForDag = (dag: AirflowDagListItem): DagTaskSeed[] => {
  const legacyTaskSeeds = DAG_TASK_PROFILE_MAP[dag.dag_id as keyof typeof DAG_TASK_PROFILE_MAP];
  const singleTaskSeeds = DEFAULT_TASK_SEEDS_BY_MATURITY[getDagMaturity(dag)];

  return singleTaskSeeds.length > 0 ? singleTaskSeeds : (legacyTaskSeeds ?? []);
};

const createTaskStateMap = (taskSeeds: DagTaskSeed[], states: AirflowTaskInstanceState[]): DagMockRunStateMatrix =>
  Object.fromEntries(taskSeeds.map((taskSeed, index) => [taskSeed.taskId, states[index] ?? null]));

const createTaskStateMapByTaskId = (
  taskSeeds: DagTaskSeed[],
  overrides: Partial<Record<string, AirflowTaskInstanceState | null>>,
  fallbackState: AirflowTaskInstanceState | null = null
): DagMockRunStateMatrix =>
  Object.fromEntries(taskSeeds.map((taskSeed) => [taskSeed.taskId, overrides[taskSeed.taskId] ?? fallbackState]));

const createSuccessTaskStates = (taskSeeds: DagTaskSeed[]): DagMockRunStateMatrix =>
  createTaskStateMapByTaskId(taskSeeds, {}, 'success');

const createFailedTaskStates = (taskSeeds: DagTaskSeed[]): DagMockRunStateMatrix => {
  if (taskSeeds.length === 0) return {};
  if (taskSeeds.length === 1) return createTaskStateMap(taskSeeds, ['failed']);

  return createTaskStateMapByTaskId(
    taskSeeds,
    Object.fromEntries(
      taskSeeds.map((taskSeed, index) => [
        taskSeed.taskId,
        index === 0 ? 'success' : index === 1 ? 'failed' : 'upstream_failed',
      ])
    ),
    null
  );
};

const createRunningTaskStates = (taskSeeds: DagTaskSeed[]): DagMockRunStateMatrix => {
  if (taskSeeds.length === 0) return {};
  if (taskSeeds.length === 1) return createTaskStateMap(taskSeeds, ['running']);
  if (taskSeeds.length === 2) return createTaskStateMap(taskSeeds, ['success', 'running']);

  return createTaskStateMapByTaskId(
    taskSeeds,
    Object.fromEntries(
      taskSeeds.map((taskSeed, index) => [
        taskSeed.taskId,
        index < taskSeeds.length - 2 ? 'success' : index === taskSeeds.length - 2 ? 'running' : 'queued',
      ])
    ),
    null
  );
};

const createQueuedTaskStates = (taskSeeds: DagTaskSeed[]): DagMockRunStateMatrix => {
  if (taskSeeds.length === 0) return {};
  if (taskSeeds.length === 1) return createTaskStateMap(taskSeeds, ['queued']);

  return createTaskStateMapByTaskId(
    taskSeeds,
    Object.fromEntries(
      taskSeeds.map((taskSeed, index) => [taskSeed.taskId, index === taskSeeds.length - 1 ? 'queued' : 'success'])
    ),
    null
  );
};

const createLatestRunTaskStates = (
  latestState: string | undefined,
  taskSeeds: DagTaskSeed[]
): DagMockRunStateMatrix => {
  if (latestState === 'failed') return createFailedTaskStates(taskSeeds);
  if (latestState === 'running') return createRunningTaskStates(taskSeeds);
  if (latestState === 'queued') return createQueuedTaskStates(taskSeeds);

  return createSuccessTaskStates(taskSeeds);
};

const getTaskDisplayName = (taskId: string) => taskId.replace(/_/g, ' ');

const DAG_IMMUTABLE_HEADER_CODE = `#################### onedata header ####################
from __future__ import annotations
import json;from pyiceberg.catalog import load_catalog;from onedata.catalog import OpenMetadataCataloger;from onedata.datalake import DataLakeManager;from onedata.paramrs import ParamRSManager

CONFIG=json.load(open("/opt/app/config.json",encoding="utf-8")) or {}
create_catalog=lambda n:load_catalog(n,uri=CONFIG["iceberg_config"][n]["in-cluster"]["rest_catalog"]["uri"],**{"s3.endpoint":CONFIG["iceberg_config"][n]["in-cluster"]["rest_catalog"]["s3_endpoint"],"s3.access-key-id":CONFIG["iceberg_config"][n]["in-cluster"]["rest_catalog"]["s3_access_key_id"],"s3.secret-access-key":CONFIG["iceberg_config"][n]["in-cluster"]["rest_catalog"]["s3_secret_access_key"],"py-io-impl":"pyiceberg.io.pyarrow.PyArrowFileIO"})
bronze_mgr,silver_mgr,gold_mgr=[create_catalog(t) for t in("bronze","silver","gold")]
cataloger=OpenMetadataCataloger(url=CONFIG["openmetadata_config"]["url"],token=CONFIG["openmetadata_config"]["token"])
datalake_mgr=DataLakeManager(data_cataloger=cataloger)
datalake_mgr.lges_meta_db_connection_info={"dbname":CONFIG["database_config"]["lges_metadata"]["in-cluster"]["database"],"user":CONFIG["database_config"]["lges_metadata"]["in-cluster"]["user"],"password":CONFIG["database_config"]["lges_metadata"]["in-cluster"]["password"],"host":CONFIG["database_config"]["lges_metadata"]["in-cluster"]["host"],"port":str(CONFIG["database_config"]["lges_metadata"]["in-cluster"]["port"])}
datalake_mgr._s3_connection_info.update({"endpoint_url":CONFIG["s3_config"]["in-cluster"]["endpoint_url"],"aws_access_key_id":CONFIG["s3_config"]["in-cluster"]["aws_access_key_id"],"aws_secret_access_key":CONFIG["s3_config"]["in-cluster"]["aws_secret_access_key"],"region_name":CONFIG["s3_config"]["in-cluster"].get("region_name","us-east-1")})
paramrs_mgr=ParamRSManager(registry_uri=CONFIG["param_registry_config"]["in-cluster"]["registry_uri"])
`;

type DagCodeTemplateContext = {
  dagId: string;
  name: string;
  maturity: DagMaturity;
  dataSourceId: string | null | undefined;
  tags: string[];
};

const toPythonString = (value: string) => JSON.stringify(value);

const toIcebergIdentifier = (value: string) => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || 'dag';
};

const toTitleCaseMaturity = (maturity: DagMaturity) => `${maturity.charAt(0).toUpperCase()}${maturity.slice(1)}`;

const uniqueTags = (tags: string[]) => {
  const seenTags = new Set<string>();

  return tags.filter((tag) => {
    const normalizedTag = tag.trim();
    const key = normalizedTag.toLowerCase();

    if (!normalizedTag || seenTags.has(key)) {
      return false;
    }

    seenTags.add(key);
    return true;
  });
};

const getInitialDefinitionByDagId = (dagId: string) => {
  return INITIAL_DAG_DEFINITIONS.find((definition) => definition.dagId === dagId);
};

const getTemplateContextFromDag = (
  dag: AirflowDagListItem,
  contextOverride?: Partial<Omit<DagCodeTemplateContext, 'dagId'>>
): DagCodeTemplateContext => {
  const dashboardItem = dashboardDagStore.find((item) => item.dagUuid === dag.dag_id);
  const initialDefinition = getInitialDefinitionByDagId(dag.dag_id);
  const dagTagNames = dag.tags?.map((tag) => tag.name) ?? [];
  const maturity =
    contextOverride?.maturity ?? dashboardItem?.maturity ?? initialDefinition?.maturity ?? getDagMaturity(dag);
  const sourceId =
    contextOverride?.dataSourceId ??
    dashboardItem?.dataSourceId ??
    initialDefinition?.upstreamDatasetIds?.join(', ') ??
    initialDefinition?.outputTables.join(', ') ??
    dag.dag_display_name ??
    dag.dag_id;

  return {
    dagId: dag.dag_id,
    name:
      contextOverride?.name ??
      dashboardItem?.name ??
      initialDefinition?.dagDisplayName ??
      dag.dag_display_name ??
      dag.dag_id,
    maturity,
    dataSourceId: sourceId,
    tags: contextOverride?.tags ?? dashboardItem?.tags ?? dagTagNames,
  };
};

const createDagTemplateBodyCode = ({ dagId, name, maturity, dataSourceId, tags }: DagCodeTemplateContext): string => {
  const normalizedTableName = toIcebergIdentifier(name);
  const runTaskId = getRunTaskIdByMaturity(maturity);
  const templateTags = uniqueTags(['template', 'parquet', 'mongodb', maturity, ...tags]);

  return `"""Auto-generated ${toTitleCaseMaturity(maturity)} DAG placeholder.

DAG UUID: ${dagId}
Maturity: ${toTitleCaseMaturity(maturity)}
Created by Cyclone.
"""

from datetime import datetime, timedelta

from airflow.models import Variable
from airflow.sdk import dag, task
from table_naming import sanitize_iceberg_table_name

# 먼저 바꿔야 할 기본 적재 대상
TARGET_NAMESPACE = ${toPythonString(name)}
TARGET_TABLE_NAME = ${toPythonString(name)}
# TARGET_TIER에는 "bronze", "silver", "gold"만 사용할 수 있습니다.
TARGET_TIER = ${toPythonString(maturity)}

# 원본 시스템 기본값
SOURCE_S3_CONN_ID = "dev_ua_preprocessing"
SOURCE_S3_BUCKET = "dev-ua-ingestion"
SOURCE_MONGO_CONN_ID = "dev_mongodb"
SOURCE_MONGO_COLLECTION = "raw_metadata"
SOURCE_ASSET_URI = ${toPythonString(dataSourceId?.trim() || name)}
SOURCE_MAX_FILES_PER_RUN = 50
SOURCE_LOOKBACK_HOURS = 24
SOURCE_USE_INCREMENTAL = False

@dag(
    dag_id=${toPythonString(dagId)},
    start_date=datetime(2025, 1, 1),
    catchup=False,
    max_active_runs=1,
    description="Template: load parquet files discovered from Mongo metadata into an Iceberg tier.",
    render_template_as_native_obj=True,
    tags=${JSON.stringify(templateTags)},
)

def template_parquet_mongodb_to_${maturity}():
    """
    ETL 흐름:
    1. Extract: Mongo 메타데이터와 parquet 원본을 읽는다.
    2. Transform: ${maturity} 적재 형태로 컬럼을 맞춘다.
    3. Load: Iceberg 에 적재하고 증분 Variable 을 갱신한다.
    """

    @task
    def ${runTaskId}() -> dict:
        from io import BytesIO

        from airflow.models import Connection
        from airflow.providers.amazon.aws.hooks.s3 import S3Hook
        from dateutil import parser
        import pyarrow as pa
        from pyarrow import parquet as pq
        from pymongo import MongoClient

        def extract() -> dict:
            asset_uri = (SOURCE_ASSET_URI or "").strip()
            if not asset_uri:
                raise ValueError("SOURCE_ASSET_URI 를 설정해야 합니다.")

            requested_namespace = (TARGET_NAMESPACE or "").strip()
            if requested_namespace:
                namespace = sanitize_iceberg_table_name(requested_namespace)
            else:
                namespace_parts = [part.strip() for part in asset_uri.split("/") if part.strip()]
                if not namespace_parts:
                    raise ValueError("namespace 를 유도할 수 없습니다. target_namespace 또는 asset_uri 를 확인하세요.")
                namespace = sanitize_iceberg_table_name(namespace_parts[0])

            requested_table_name = (TARGET_TABLE_NAME or "").strip()
            if not requested_table_name:
                raise ValueError("TARGET_TABLE_NAME 을 설정해야 합니다.")
            table_name = sanitize_iceberg_table_name(requested_table_name)

            target_tier = (TARGET_TIER or "").strip()
            variable_key = "template_last_ingested_timestamp_${normalizedTableName}"
            use_incremental = bool(SOURCE_USE_INCREMENTAL)
            max_files_per_run = int(SOURCE_MAX_FILES_PER_RUN)
            lookback_hours = int(SOURCE_LOOKBACK_HOURS)

            mongo_conn = Connection.get_connection_from_secrets(SOURCE_MONGO_CONN_ID)
            mongo_kwargs = {
                "host": mongo_conn.host,
                "port": mongo_conn.port,
                "serverSelectionTimeoutMS": 3000,
            }
            if (mongo_conn.login or "").strip() and (mongo_conn.password or "").strip():
                mongo_kwargs["username"] = mongo_conn.login
                mongo_kwargs["password"] = mongo_conn.password or ""
                mongo_kwargs["authSource"] = "admin"

            collection = MongoClient(**mongo_kwargs)[mongo_conn.schema][SOURCE_MONGO_COLLECTION]

            if use_incremental:
                try:
                    last_timestamp_str = Variable.get(variable_key)
                except Exception:
                    last_timestamp_str = "2000-01-01T00:00:00Z"
                    Variable.set(variable_key, last_timestamp_str)
                query = {
                    "asset_uri": asset_uri,
                    "acquired_at": {"$gt": parser.isoparse(last_timestamp_str.replace("Z", "+00:00"))},
                }
            else:
                query = {
                    "asset_uri": asset_uri,
                    "acquired_at": {"$gte": datetime.utcnow() - timedelta(hours=lookback_hours)},
                }

            documents = list(
                collection.find(query, {"_id": 0}).sort("acquired_at", 1).limit(max_files_per_run)
            )
            for document in documents:
                if not (document.get("storage_key") or "").strip():
                    raise ValueError("메타데이터에 storage_key 가 없는 문서가 있습니다.")

            latest_acquired_at = None
            if documents:
                acquired_at = documents[-1].get("acquired_at")
                latest_acquired_at = (
                    acquired_at.strftime("%Y-%m-%dT%H:%M:%S.%f")[:-3] + "Z"
                    if hasattr(acquired_at, "strftime")
                    else str(acquired_at)
                )

            logger.info("Extract 완료: %s documents", len(documents))
            return {
                "documents": documents,
                "target_namespace": namespace,
                "target_table_name": table_name,
                "target_tier": target_tier,
                "variable_key": variable_key,
                "latest_acquired_at": latest_acquired_at,
            }

        def transform(extracted: dict) -> dict:
            documents = extracted.get("documents") or []
            if not documents:
                logger.warning("처리할 메타데이터가 없어 ${maturity} 적재를 건너뜁니다.")
                extracted["tables"] = []
                return extracted

            s3_hook = S3Hook(aws_conn_id=SOURCE_S3_CONN_ID)
            s3_conn = s3_hook.get_conn()
            transformed_tables = []

            for document in documents:
                body = s3_conn.get_object(
                    Bucket=SOURCE_S3_BUCKET,
                    Key=document["storage_key"],
                )["Body"].read()
                if not body:
                    logger.warning(
                        "비어 있는 parquet 객체는 건너뜁니다: s3://%s/%s", SOURCE_S3_BUCKET, document["storage_key"]
                    )
                    continue

                table = pq.read_table(BytesIO(body))
                if "data_key" in table.column_names:
                    table = table.drop_columns(["data_key"])

                data_key = (document.get("asset_uri") or SOURCE_ASSET_URI or "").strip()
                table = table.append_column(
                    "data_key",
                    pa.array([data_key] * table.num_rows, type=pa.string()),
                )
                ordered_columns = ["data_key"] + [name for name in table.column_names if name != "data_key"]
                transformed_tables.append(table.select(ordered_columns))

            logger.info("Transform 완료: %s files", len(transformed_tables))
            extracted["tables"] = transformed_tables
            return extracted

        def load(transformed: dict) -> dict:
            tables = transformed.get("tables") or []
            if not tables:
                return {
                    "saved_count": 0,
                    "namespace": transformed["target_namespace"],
                    "table": transformed["target_table_name"],
                    "tier": transformed["target_tier"],
                    "error": "No transformed rows",
                }

            if transformed["target_tier"] == "bronze":
                target_mgr = bronze_mgr
            elif transformed["target_tier"] == "silver":
                target_mgr = silver_mgr
            elif transformed["target_tier"] == "gold":
                target_mgr = gold_mgr
            else:
                raise ValueError(f"지원하지 않는 TARGET_TIER 입니다: {transformed['target_tier']!r}")

            namespaces = target_mgr.list_all_namespaces()
            if transformed["target_namespace"] not in [item[0] for item in namespaces]:
                target_mgr.create_namespace(transformed["target_namespace"])

            tables_in_namespace = target_mgr.list_all_tables(transformed["target_namespace"]) or []
            table_exists = transformed["target_table_name"] in [item[1] for item in tables_in_namespace]
            total_saved_rows = 0

            for table in tables:
                if table.num_rows == 0:
                    continue

                if table_exists:
                    target_mgr.append(
                        transformed["target_namespace"],
                        transformed["target_table_name"],
                        table,
                    )
                else:
                    target_mgr.create_table(
                        namespace=transformed["target_namespace"],
                        table_name=transformed["target_table_name"],
                        sample_data=table,
                        append_sample_data=True,
                    )
                    table_exists = True

                total_saved_rows += table.num_rows

            if total_saved_rows > 0 and transformed.get("latest_acquired_at"):
                Variable.set(transformed["variable_key"], transformed["latest_acquired_at"])

            logger.info(
                "Load 완료: %s.%s (%s rows)",
                transformed["target_namespace"],
                transformed["target_table_name"],
                total_saved_rows,
            )
            return {
                "saved_count": total_saved_rows,
                "namespace": transformed["target_namespace"],
                "table": transformed["target_table_name"],
                "tier": transformed["target_tier"],
            }

        extracted = extract()
        transformed = transform(extracted)
        return load(transformed)

    ${runTaskId}()


# Airflow DAG discovery 를 위해 모듈 import 시 DAG 객체를 생성합니다.
template_parquet_mongodb_to_${maturity}()
`;
};

const TABLE_NAMING_LIBRARY_CONTENT = `def sanitize_iceberg_table_name(name: str) -> str:
    return name.strip().lower().replace(" ", "_")
`;

const standardLibrary = (name: string): DagInstalledLibrary => ({
  name,
  type: 'standard',
});

const tableNamingLibrary = (): DagInstalledLibrary => ({
  name: 'table_naming',
  type: 'custom',
  content: TABLE_NAMING_LIBRARY_CONTENT,
});

const createInitialInstalledLibraries = (maturity: DagMaturity, versionLabel: string): DagInstalledLibrary[] => {
  const [majorVersionText] = versionLabel.split('.');
  const majorVersion = Number(majorVersionText);
  const common = [standardLibrary('pandas'), standardLibrary('pyarrow'), tableNamingLibrary()];

  if (maturity === DAG_MATURITY_TYPES.BRONZE) {
    return majorVersion <= 2
      ? [standardLibrary('pyarrow'), standardLibrary('pymongo'), tableNamingLibrary()]
      : [...common, standardLibrary('pymongo')];
  }

  if (maturity === DAG_MATURITY_TYPES.SILVER) {
    return majorVersion <= 2 ? common : [...common, standardLibrary('pyiceberg')];
  }

  return majorVersion <= 4
    ? [...common, standardLibrary('pyiceberg'), standardLibrary('requests')]
    : [...common, standardLibrary('pyiceberg'), standardLibrary('requests'), standardLibrary('ray')];
};

const createDagVersionCode = ({
  dagId,
  dagDisplayName,
  versionLabel,
  maturity,
  dataSourceId,
  tags,
}: {
  dagId: string;
  dagDisplayName: string;
  versionLabel: string;
  maturity: DagMaturity;
  dataSourceId?: string | null;
  tags: string[];
}): DagCodeVersion => {
  return {
    versionLabel,
    bodyCode: createDagTemplateBodyCode({
      dagId,
      name: dagDisplayName,
      maturity,
      dataSourceId,
      tags,
    }),
    importedLibraries: [
      { module: 'airflow.sdk.dag' },
      { module: 'airflow.sdk.task' },
      { module: 'airflow.models.Variable' },
      { module: 'airflow.models.Connection' },
      { module: 'airflow.providers.amazon.aws.hooks.s3.S3Hook' },
      { module: 'datetime.datetime' },
      { module: 'datetime.timedelta' },
      { module: 'dateutil.parser' },
      { module: 'pyarrow' },
      { module: 'pyarrow.parquet' },
      { module: 'pymongo.MongoClient' },
      { module: 'table_naming.sanitize_iceberg_table_name' },
    ],
    availableLibraries: DEFAULT_AVAILABLE_DAG_VERSION_LIBRARIES,
    installedLibraries: createInitialInstalledLibraries(maturity, versionLabel),
  };
};

const createDagCodeVersions = (
  dag: AirflowDagListItem,
  contextOverride?: Partial<Omit<DagCodeTemplateContext, 'dagId'>>
): DagCodeVersion[] => {
  const templateContext = getTemplateContextFromDag(dag, contextOverride);

  return (dag.version_snapshots ?? []).reduce<DagCodeVersion[]>((acc, snapshot) => {
    const versionLabel = normalizeVersionLabel(snapshot.bundle_version);

    if (!versionLabel) {
      return acc;
    }

    acc.push(
      createDagVersionCode({
        dagId: dag.dag_id,
        dagDisplayName: templateContext.name,
        versionLabel,
        maturity: templateContext.maturity,
        dataSourceId: templateContext.dataSourceId,
        tags: [...snapshot.tags],
      })
    );

    return acc;
  }, []);
};

const getShiftedIsoDate = (baseIsoDate: string, dayOffset: number) => {
  return new Date(new Date(baseIsoDate).getTime() - dayOffset * DAY_IN_MILLISECONDS).toISOString();
};

const toOffsetMilliseconds = ({
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
}: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}) => {
  return days * DAY_IN_MILLISECONDS + hours * HOUR_IN_MILLISECONDS + minutes * MINUTE_IN_MILLISECONDS + seconds * 1000;
};

type DagRunTimeProfile = {
  lastOffsetMs: number;
  nextOffsetMs: number;
};

const INITIAL_DAG_RUN_TIME_PROFILES: Record<string, DagRunTimeProfile> = {
  bronze_gsp_ecms1_waveform_ingest: {
    lastOffsetMs: toOffsetMilliseconds({ minutes: 23, seconds: 18 }),
    nextOffsetMs: toOffsetMilliseconds({ minutes: 47, seconds: 25 }),
  },
  bronze_kmp_waveform_ingest: {
    lastOffsetMs: toOffsetMilliseconds({ hours: 2, minutes: 11, seconds: 9 }),
    nextOffsetMs: toOffsetMilliseconds({ hours: 1, minutes: 38, seconds: 41 }),
  },
  silver_gsp_ecms1_curation: {
    lastOffsetMs: toOffsetMilliseconds({ hours: 5, minutes: 42, seconds: 33 }),
    nextOffsetMs: toOffsetMilliseconds({ hours: 3, minutes: 21, seconds: 12 }),
  },
  silver_kmp_ecms_curation: {
    lastOffsetMs: toOffsetMilliseconds({ hours: 11, minutes: 6, seconds: 52 }),
    nextOffsetMs: toOffsetMilliseconds({ hours: 5, minutes: 4, seconds: 7 }),
  },
  gold_gsp_ecms1_feature_publish: {
    lastOffsetMs: toOffsetMilliseconds({ hours: 20, minutes: 18, seconds: 44 }),
    nextOffsetMs: toOffsetMilliseconds({ hours: 8, minutes: 45, seconds: 29 }),
  },
  gold_kmp_ecms_feature_publish: {
    lastOffsetMs: toOffsetMilliseconds({ hours: 28, minutes: 9, seconds: 11 }),
    nextOffsetMs: toOffsetMilliseconds({ hours: 22, minutes: 36, seconds: 5 }),
  },
};

const createUtcDateAtMinute = (baseDate: Date, hour: number, minute: number) => {
  return new Date(
    Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate(), hour, minute, 0, 0)
  );
};

const getIntervalMinuteRunWindow = (now: Date, intervalMinutes: number) => {
  const intervalMilliseconds = intervalMinutes * MINUTE_IN_MILLISECONDS;
  const lastRunAt = new Date(Math.floor(now.getTime() / intervalMilliseconds) * intervalMilliseconds);
  const nextDagrunLogicalDate = new Date(lastRunAt.getTime() + intervalMilliseconds);

  return {
    lastRunAt: lastRunAt.toISOString(),
    nextDagrunLogicalDate: nextDagrunLogicalDate.toISOString(),
  };
};

const getHourlyMinuteRunWindow = (now: Date, minute: number, hourInterval = 1) => {
  const currentHour = now.getUTCHours();
  const candidateHour = Math.floor(currentHour / hourInterval) * hourInterval;
  const candidate = createUtcDateAtMinute(now, candidateHour, minute);
  const intervalMilliseconds = hourInterval * HOUR_IN_MILLISECONDS;
  const isCandidateFuture = candidate.getTime() > now.getTime();
  const lastRunAt = new Date(candidate.getTime() - (isCandidateFuture ? intervalMilliseconds : 0));
  const nextDagrunLogicalDate = new Date(candidate.getTime() + (isCandidateFuture ? 0 : intervalMilliseconds));

  return {
    lastRunAt: lastRunAt.toISOString(),
    nextDagrunLogicalDate: nextDagrunLogicalDate.toISOString(),
  };
};

const getFallbackRunWindow = (now: Date) => ({
  lastRunAt: new Date(now.getTime() - HOUR_IN_MILLISECONDS).toISOString(),
  nextDagrunLogicalDate: new Date(now.getTime() + HOUR_IN_MILLISECONDS).toISOString(),
});

const getProfiledRunWindow = (now: Date, profile: DagRunTimeProfile) => ({
  lastRunAt: new Date(now.getTime() - profile.lastOffsetMs).toISOString(),
  nextDagrunLogicalDate: new Date(now.getTime() + profile.nextOffsetMs).toISOString(),
});

const getRunWindowFromTimetable = (timetableSummary: string, now: Date) => {
  const intervalMinuteMatch = timetableSummary.match(/^\*\/(\d+) \* \* \* \*$/);
  if (intervalMinuteMatch) {
    const intervalMinutes = Number(intervalMinuteMatch[1]);

    if (Number.isFinite(intervalMinutes) && intervalMinutes > 0) {
      return getIntervalMinuteRunWindow(now, intervalMinutes);
    }
  }

  const hourlyMinuteMatch = timetableSummary.match(/^(\d+) \* \* \* \*$/);
  if (hourlyMinuteMatch) {
    const minute = Number(hourlyMinuteMatch[1]);

    if (Number.isFinite(minute)) {
      return getHourlyMinuteRunWindow(now, minute);
    }
  }

  const intervalHourMatch = timetableSummary.match(/^(\d+) \*\/(\d+) \* \* \*$/);
  if (intervalHourMatch) {
    const minute = Number(intervalHourMatch[1]);
    const hourInterval = Number(intervalHourMatch[2]);

    if (Number.isFinite(minute) && Number.isFinite(hourInterval) && hourInterval > 0) {
      return getHourlyMinuteRunWindow(now, minute, hourInterval);
    }
  }

  return getFallbackRunWindow(now);
};

const resolveDagFixtureDefinition = (definition: DagFixtureDefinition, now: Date): ResolvedDagFixtureDefinition => ({
  ...definition,
  ...(INITIAL_DAG_RUN_TIME_PROFILES[definition.dagId]
    ? getProfiledRunWindow(now, INITIAL_DAG_RUN_TIME_PROFILES[definition.dagId])
    : getRunWindowFromTimetable(definition.timetableSummary, now)),
});

const createRunId = (dagId: string, runNumber: number) => {
  return `${dagId}-run-${String(runNumber).padStart(2, '0')}`;
};

const getHistoricalRunPatterns = (taskSeeds: DagTaskSeed[]): DagRunPattern[] => [
  {
    duration: 360,
    runType: 'scheduled',
    state: 'success',
    triggeredBy: 'timetable',
    triggeringUserName: null,
    taskStates: createSuccessTaskStates(taskSeeds),
  },
  {
    duration: 515,
    runType: 'manual',
    state: 'success',
    triggeredBy: 'ui',
    triggeringUserName: 'opds-admin',
    taskStates: createSuccessTaskStates(taskSeeds),
  },
  {
    duration: 680,
    runType: 'backfill',
    state: 'failed',
    triggeredBy: 'backfill',
    triggeringUserName: 'scheduler',
    taskStates: createFailedTaskStates(taskSeeds),
  },
  {
    duration: 430,
    runType: 'scheduled',
    state: 'queued',
    triggeredBy: 'timetable',
    triggeringUserName: null,
    taskStates: createQueuedTaskStates(taskSeeds),
  },
  {
    duration: 455,
    runType: 'scheduled',
    state: 'running',
    triggeredBy: 'timetable',
    triggeringUserName: null,
    taskStates: createRunningTaskStates(taskSeeds),
  },
  {
    duration: 390,
    runType: 'manual',
    state: 'success',
    triggeredBy: 'ui',
    triggeringUserName: 'data-operator',
    taskStates: createSuccessTaskStates(taskSeeds),
  },
];

const createVersionSnapshot = ({
  bundleVersion,
  description,
  timetableSummary,
  tags,
  runStatus,
  lastRunAt,
  nextDagrunRunAfter,
}: {
  bundleVersion: string;
  description: string;
  timetableSummary: string;
  tags: string[];
  runStatus?: AirflowDagListRunStatus;
  lastRunAt?: string;
  nextDagrunRunAfter?: string;
}): AirflowDagVersionSnapshot => ({
  bundle_version: bundleVersion,
  description,
  timetable_summary: timetableSummary,
  tags,
  run_status: runStatus,
  last_run_at: lastRunAt,
  next_dagrun_run_after: nextDagrunRunAfter,
});

const getVersionNumberFromBundleVersion = (bundleVersion: string) => Number(bundleVersion.match(/^v(\d+)/)?.[1] ?? 1);

const toAirflowRunState = (status: DagListRunStatus): AirflowDagRunState => {
  if (status === 'failure') return 'failed';
  if (status === 'running') return 'running';
  if (status === 'success') return 'success';
  return 'queued';
};

const getHistoricalStatusForVersion = (index: number): DagListRunStatus => {
  if (index === 1) return 'success';
  if (index === 2) return 'draft';
  return index % 2 === 0 ? 'success' : 'pending';
};

const createInitialVersionSnapshots = (definition: ResolvedDagFixtureDefinition): AirflowDagVersionSnapshot[] => {
  return definition.versionHistory.map((bundleVersion, index) => {
    const isLatestVersion = index === 0;
    const lastRunAt = isLatestVersion
      ? definition.lastRunAt
      : definition.lastRunAt
        ? getShiftedIsoDate(definition.lastRunAt, index + 1)
        : undefined;

    return createVersionSnapshot({
      bundleVersion,
      description: isLatestVersion
        ? definition.description
        : (definition.previousVersionDescriptions[index - 1] ?? definition.description),
      timetableSummary: definition.timetableSummary,
      tags: [definition.maturity, ...definition.tags],
      runStatus: isLatestVersion ? definition.latestRunStatus : getHistoricalStatusForVersion(index),
      lastRunAt,
      nextDagrunRunAfter: isLatestVersion ? definition.nextDagrunLogicalDate : undefined,
    });
  });
};

const validateDagFixtureDefinition = (definition: DagFixtureDefinition) => {
  if (definition.versionHistory.length === 0) {
    throw new Error(`[DAG Mock] versionHistory is required: ${definition.dagId}`);
  }

  const tableNameSet = TABLE_NAMES_BY_MATURITY[definition.maturity];
  const missingTables = definition.outputTables.filter((tableName) => !tableNameSet.has(tableName));

  if (missingTables.length > 0) {
    throw new Error(`[DAG Mock] output table not found in ${definition.maturity} data: ${missingTables.join(', ')}`);
  }
};

const createInitialDagListItem = (definition: ResolvedDagFixtureDefinition): AirflowDagListItem => ({
  dag_id: definition.dagId,
  dag_display_name: definition.dagDisplayName,
  is_paused: definition.isPaused,
  description: definition.description,
  timetable_summary: definition.timetableSummary,
  owners: definition.owners,
  tags: [{ name: definition.maturity }, ...definition.tags.map((tag) => ({ name: tag }))],
  has_import_errors: definition.hasImportErrors,
  next_dagrun_logical_date: definition.nextDagrunLogicalDate,
  latest_dag_runs: definition.lastRunAt
    ? [
        {
          start_date: definition.lastRunAt,
          run_after: definition.lastRunAt,
          state: toAirflowRunState(definition.latestRunStatus),
        },
      ]
    : [],
  version_snapshots: createInitialVersionSnapshots(definition),
});

const createDagDashboardItem = (definition: ResolvedDagFixtureDefinition): DagDashboardResponse => {
  const latestVersion = getVersionNumberFromBundleVersion(definition.versionHistory[0] ?? 'v1.0.0');

  return {
    dagUuid: definition.dagId,
    name: definition.dagDisplayName,
    description: definition.description,
    maturity: definition.maturity,
    entityType: definition.outputTables.join(', '),
    dataSourceId: definition.upstreamDatasetIds?.join(', ') ?? definition.outputTables.join(', '),
    isSchedulingEnabled: !definition.isPaused,
    hasDeployment: true,
    deployedVersion: latestVersion,
    latestVersion,
    schedule: definition.timetableSummary,
    nextRun: definition.nextDagrunLogicalDate ?? null,
    versions: createDashboardVersions(
      ...definition.versionHistory.map((bundleVersion, index) => ({
        version: getVersionNumberFromBundleVersion(bundleVersion),
        status: index === 0 ? definition.latestRunStatus : getHistoricalStatusForVersion(index),
        lastRun:
          index === 0
            ? (definition.lastRunAt ?? null)
            : definition.lastRunAt
              ? getShiftedIsoDate(definition.lastRunAt, index + 1)
              : null,
      }))
    ),
    tags: [definition.maturity, ...definition.tags],
    isBookmarked: false,
    createdAt: getShiftedIsoDate(definition.lastRunAt ?? '2026-03-27T00:00:00Z', 21),
    updatedAt: definition.lastRunAt ?? '2026-03-27T00:00:00Z',
  };
};

const INITIAL_DAG_DEFINITIONS: DagFixtureDefinition[] = [
  {
    dagId: 'bronze_gsp_ecms1_waveform_ingest',
    dagDisplayName: 'Bronze GSP ECMS1 Waveform Ingest',
    maturity: 'bronze',
    description:
      'OPC-UA GSP ECMS1 원천에서 waveform을 수집해 Bronze gsp_ecms1 chart/trend/bearing 데이터를 적재합니다.',
    timetableSummary: '*/5 * * * *',
    owners: ['data-platform', 'DI'],
    tags: ['gsp', 'waveform'],
    versionHistory: ['v4.0.0', 'v3.0.0', 'v2.0.0'],
    isPaused: false,
    latestRunStatus: 'success',
    hasImportErrors: false,
    outputTables: ['gsp_ecms1_chart', 'gsp_ecms1_trend', 'gsp_ecms1_bearings1', 'gsp_ecms1_bearings2'],
    previousVersionDescriptions: [
      'GSP ECMS1 waveform ingestion tuned for partition-aware chart/trend writes.',
      'Initial GSP ECMS1 waveform ingestion blueprint before OPC-UA retry hardening.',
    ],
  },
  {
    dagId: 'bronze_kmp_waveform_ingest',
    dagDisplayName: 'Bronze KMP ECMS Waveform Ingest',
    maturity: 'bronze',
    description: 'KMP ECMS 원천에서 chart, trend, bearing 데이터를 수집해 Bronze 레이어에 적재합니다.',
    timetableSummary: '*/15 * * * *',
    owners: ['data-platform', 'DI'],
    tags: ['kmp', 'waveform'],
    versionHistory: ['v3.0.0', 'v2.0.0', 'v1.0.0'],
    isPaused: false,
    latestRunStatus: 'running',
    hasImportErrors: false,
    outputTables: ['kmp_ecms_chart', 'kmp_ecms_trend', 'kmp_ecms_bearings1'],
    previousVersionDescriptions: [
      'KMP ECMS waveform ingestion with resampling and trend aggregation split into separate tasks.',
      'Legacy KMP ECMS ingestion before waveform resampling was added.',
    ],
  },
  {
    dagId: 'silver_gsp_ecms1_curation',
    dagDisplayName: 'Silver GSP ECMS1 Curation',
    maturity: 'silver',
    description:
      'Bronze GSP ECMS1 데이터를 표준화해 Silver gsp_ecms1_waveform_preprocessed와 gsp_ecms1_config를 생성합니다.',
    timetableSummary: '10 * * * *',
    owners: ['data-platform', 'DI'],
    tags: ['gsp', 'curation'],
    versionHistory: ['v4.0.0', 'v3.0.0', 'v2.0.0'],
    isPaused: false,
    latestRunStatus: 'success',
    hasImportErrors: false,
    outputTables: ['gsp_ecms1_waveform_preprocessed', 'gsp_ecms1_config'],
    upstreamDatasetIds: ['gsp_ecms1_chart', 'gsp_ecms1_trend', 'gsp_ecms1_bearings1', 'gsp_ecms1_bearings2'],
    previousVersionDescriptions: [
      'GSP Silver curation after bearing master joins were added.',
      'Legacy GSP Silver curation before standardized quality validation.',
    ],
  },
  {
    dagId: 'silver_kmp_ecms_curation',
    dagDisplayName: 'Silver KMP ECMS Curation',
    maturity: 'silver',
    description:
      'Bronze KMP ECMS 데이터를 표준화해 Silver kmp_ecms_waveform_preprocessed와 kmp_ecms_config를 생성합니다.',
    timetableSummary: '25 * * * *',
    owners: ['data-platform', 'DI'],
    tags: ['kmp', 'curation'],
    versionHistory: ['v4.0.0', 'v3.0.0', 'v2.0.0'],
    isPaused: false,
    latestRunStatus: 'running',
    hasImportErrors: false,
    outputTables: ['kmp_ecms_waveform_preprocessed', 'kmp_ecms_config'],
    upstreamDatasetIds: ['kmp_ecms_chart', 'kmp_ecms_trend', 'kmp_ecms_bearings1'],
    previousVersionDescriptions: [
      'KMP Silver curation after waveform denoising and config enrichment were parallelized.',
      'Legacy KMP Silver curation before standardized sample-rate normalization was introduced.',
    ],
  },
  {
    dagId: 'gold_gsp_ecms1_feature_publish',
    dagDisplayName: 'Gold GSP ECMS1 Feature Publish',
    maturity: 'gold',
    description:
      'Silver GSP ECMS1 데이터를 기반으로 Gold gsp_ecms1_feature_preprocessed와 gsp_ecms1_diagnosis_result를 생성합니다.',
    timetableSummary: '30 */2 * * *',
    owners: ['DI', 'ml-platform'],
    tags: ['gsp', 'feature'],
    versionHistory: ['v6.0.0', 'v5.0.0', 'v4.0.0'],
    isPaused: false,
    latestRunStatus: 'failure',
    hasImportErrors: false,
    outputTables: ['gsp_ecms1_feature_preprocessed', 'gsp_ecms1_diagnosis_result'],
    upstreamDatasetIds: ['gsp_ecms1_waveform_preprocessed', 'gsp_ecms1_config'],
    previousVersionDescriptions: [
      'GSP Gold publish before diagnosis bundle deployment failed on feature-schema drift.',
      'Legacy GSP Gold publish prior to health-index threshold calibration updates.',
    ],
  },
  {
    dagId: 'gold_kmp_ecms_feature_publish',
    dagDisplayName: 'Gold KMP ECMS Feature Publish',
    maturity: 'gold',
    description:
      'Silver KMP ECMS 데이터를 기반으로 Gold kmp_ecms_feature_preprocessed와 kmp_ecms_diagnosis_results를 생성합니다.',
    timetableSummary: '45 */2 * * *',
    owners: ['DI', 'ml-platform'],
    tags: ['kmp', 'feature'],
    versionHistory: ['v5.0.0', 'v4.0.0', 'v3.0.0'],
    isPaused: false,
    latestRunStatus: 'success',
    hasImportErrors: false,
    outputTables: ['kmp_ecms_feature_preprocessed', 'kmp_ecms_diagnosis_results'],
    upstreamDatasetIds: ['kmp_ecms_waveform_preprocessed', 'kmp_ecms_config'],
    previousVersionDescriptions: [
      'KMP Gold publish after feature aggregation and diagnosis fan-out were stabilized.',
      'Legacy KMP Gold publish before diagnosis summary persistence was optimized.',
    ],
  },
];

INITIAL_DAG_DEFINITIONS.forEach(validateDagFixtureDefinition);

const createResolvedDagFixtureDefinitions = (now = new Date()) =>
  INITIAL_DAG_DEFINITIONS.map((definition) => resolveDagFixtureDefinition(definition, now));

const createInitialAirflowDags = (now = new Date()): AirflowDagListItem[] =>
  createResolvedDagFixtureDefinitions(now).map(createInitialDagListItem);

const createInitialDagDashboardItems = (now = new Date()): DagDashboardResponse[] =>
  createResolvedDagFixtureDefinitions(now).map(createDagDashboardItem);

const INITIAL_DAG_MOCK_NOW = new Date();
const INITIAL_AIRFLOW_DAGS: AirflowDagListItem[] = createInitialAirflowDags(INITIAL_DAG_MOCK_NOW);

const cloneDagListItem = (dag: AirflowDagListItem): AirflowDagListItem => ({
  ...dag,
  owners: dag.owners ? [...dag.owners] : undefined,
  tags: dag.tags?.map((tag) => ({ ...tag })),
  latest_dag_runs: dag.latest_dag_runs?.map((run) => ({ ...run })),
  version_snapshots: dag.version_snapshots?.map((snapshot) => ({
    ...snapshot,
    tags: [...snapshot.tags],
  })),
});

const cloneDagListResponse = (dags: AirflowDagListItem[]): AirflowDagListResponse => ({
  total_entries: dags.length,
  dags: dags.map(cloneDagListItem),
});

const cloneDagDetail = (dagDetail: AirflowDagDetailResponse): AirflowDagDetailResponse => ({
  ...dagDetail,
  tags: dagDetail.tags?.map((tag) => ({ ...tag })),
  owners: dagDetail.owners ? [...dagDetail.owners] : undefined,
});

const cloneDagCodeVersion = (dagCodeVersion: DagCodeVersion): DagCodeVersion => ({
  ...dagCodeVersion,
  importedLibraries: [...dagCodeVersion.importedLibraries],
  availableLibraries: dagCodeVersion.availableLibraries
    ? dagCodeVersion.availableLibraries.map((library) => ({ ...library }))
    : undefined,
  installedLibraries: dagCodeVersion.installedLibraries?.map((library) => ({ ...library })),
});

const DEFAULT_AVAILABLE_DAG_VERSION_LIBRARIES: LibraryItem[] = [
  { name: 'apache-airflow', preinstalled: true, libraryVersion: '2.10.5' },
  { name: 'onedata', preinstalled: true, libraryVersion: '1.0.0' },
  { name: 'pyiceberg', preinstalled: true, libraryVersion: '0.8.1' },
  { name: 'pyarrow', preinstalled: true, libraryVersion: '18.1.0' },
  { name: 'pymongo', preinstalled: true, libraryVersion: '4.10.1' },
  { name: 'python-dateutil', preinstalled: true, libraryVersion: '2.9.0' },
];

const versionLabelToBundleVersion = (versionLabel: string) => {
  return `v${versionLabel}.0`;
};

const createDashboardVersions = (...versions: DagVersionStatusItem[]): DagVersionStatusItem[] => versions;

const cloneDagRun = (run: AirflowDagRunListItem): AirflowDagRunListItem => ({
  ...run,
  dag_versions: run.dag_versions.map((version) => ({ ...version })),
  conf: run.conf ? { ...run.conf } : null,
});

const INITIAL_DAG_DASHBOARD_ITEMS: DagDashboardResponse[] = createInitialDagDashboardItems(INITIAL_DAG_MOCK_NOW);

const cloneDagDashboardItem = (item: DagDashboardResponse): DagDashboardResponse => ({
  ...item,
  tags: item.tags ? [...item.tags] : undefined,
  versions: item.versions?.map((version) => ({ ...version })),
});

let dashboardDagStore: DagDashboardResponse[] = INITIAL_DAG_DASHBOARD_ITEMS.map(cloneDagDashboardItem);

const getLatestDashboardVersionItem = (item: DagDashboardResponse) => {
  if (item.latestVersion != null) {
    const latestVersionItem = item.versions?.find((version) => version.version === item.latestVersion);
    if (latestVersionItem) {
      return latestVersionItem;
    }
  }

  return item.versions?.[0] ?? null;
};

const cloneDagTaskGraphResponse = (response: DagTaskGraphResponse): DagTaskGraphResponse => ({
  nodes: response.nodes.map((node) => ({ ...node })),
  edges: response.edges.map((edge) => ({ ...edge })),
});

const cloneDagVersionResponse = (response: DagVersionResponse): DagVersionResponse => ({ ...response });

const cloneDagVersionListResponse = (response: DagVersionListResponse): DagVersionListResponse => ({
  ...response,
  items: response.items.map(cloneDagVersionResponse),
});

const cloneLibraryItem = (library: LibraryItem): LibraryItem => ({ ...library });

const cloneGlobalLibraryListResponse = (response: GlobalLibraryListResponse): GlobalLibraryListResponse => ({
  ...response,
  items: response.items.map(cloneLibraryItem),
});

const cloneDagVersionCodeResponse = (response: DagVersionCodeResponse): DagVersionCodeResponse => ({
  ...response,
  installedLibraries: response.installedLibraries?.map((library) => ({ ...library })),
});

const cloneSaveDagVersionResponse = (response: SaveDagVersionResponse): SaveDagVersionResponse => ({ ...response });

const cloneDeployDagResponse = (response: DeployDagResponse): DeployDagResponse => ({ ...response });

const cloneDagTaskResponse = (task: DagTaskResponse): DagTaskResponse => ({ ...task });

const mapDashboardItemToOverview = (item: DagDashboardResponse): DagOverviewResponse => {
  const latestVersionItem = getLatestDashboardVersionItem(item);

  return {
    dagUuid: item.dagUuid,
    name: item.name,
    description: item.description ?? null,
    runType: item.schedule ?? null,
    nextRun: item.nextRun ?? null,
    lastRun: latestVersionItem?.lastRun ?? null,
    status: latestVersionItem?.status ?? 'draft',
    tags: item.tags ? [...item.tags] : [],
    maturity: item.maturity,
    owner: 'data-platform',
    deployedVersion: item.deployedVersion ?? null,
    latestVersion: item.latestVersion ?? null,
    schedule: item.schedule ?? null,
    entityType: item.entityType ?? null,
    dataSource: item.dataSourceId ?? null,
  };
};

const cloneGridRun = (run: AirflowDagGridRun): AirflowDagGridRun => ({ ...run });

const cloneDagTask = (task: AirflowDagTask): AirflowDagTask => ({
  ...task,
  execution_timeout: { ...task.execution_timeout },
  retry_delay: { ...task.retry_delay },
  template_fields: [...task.template_fields],
  downstream_task_ids: [...task.downstream_task_ids],
  extra_links: [...task.extra_links],
  params: { ...task.params },
  class_ref: { ...task.class_ref },
});

const cloneTaskSummary = (summary: AirflowDagTaskInstanceSummary): AirflowDagTaskInstanceSummary => ({
  ...summary,
  child_states: summary.child_states ? { ...summary.child_states } : undefined,
});

const normalizeDashboardFilterValue = (value?: string) => value?.trim().toLowerCase();
const compareNullableDate = (left?: string | null, right?: string | null) => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);

  if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
  if (Number.isNaN(leftTime)) return 1;
  if (Number.isNaN(rightTime)) return -1;

  return leftTime - rightTime;
};

const cloneTaskInstance = (taskInstance: AirflowTaskInstance): AirflowTaskInstance => ({
  ...taskInstance,
  dag_version: taskInstance.dag_version ? { ...taskInstance.dag_version } : null,
  rendered_fields: { ...taskInstance.rendered_fields },
  trigger: taskInstance.trigger ? { ...taskInstance.trigger } : null,
  triggerer_job: taskInstance.triggerer_job ? { ...taskInstance.triggerer_job } : null,
});

const createDagDetailResponse = (
  dag: AirflowDagListItem,
  bundleVersion: string,
  maxActiveTasks: number
): AirflowDagDetailResponse => ({
  dag_id: dag.dag_id,
  dag_display_name: dag.dag_display_name,
  is_paused: dag.is_paused,
  is_stale: false,
  last_parsed_time: '2026-03-24T00:10:00Z',
  last_parse_duration: 1.4,
  last_expired: undefined,
  bundle_name: `${dag.dag_id}-bundle`,
  bundle_version: bundleVersion,
  relative_fileloc: `dags/${dag.dag_id}.py`,
  fileloc: `/opt/airflow/dags/${dag.dag_id}.py`,
  description: dag.description,
  timetable_summary: dag.timetable_summary,
  timetable_description: dag.timetable_description,
  tags: dag.tags?.map((tag) => ({ ...tag })),
  max_active_tasks: maxActiveTasks,
  max_active_runs: 2,
  max_consecutive_failed_dag_runs: 3,
  has_task_concurrency_limits: false,
  has_import_errors: dag.has_import_errors,
  next_dagrun_logical_date: dag.next_dagrun_logical_date,
  next_dagrun_data_interval_start: dag.latest_dag_runs?.[0]?.run_after ?? dag.next_dagrun_logical_date,
  next_dagrun_data_interval_end: dag.next_dagrun_logical_date,
  next_dagrun_run_after: dag.next_dagrun_logical_date,
  owners: dag.owners ? [...dag.owners] : [],
  file_token: `${dag.dag_id}-token`,
});

const createDagTask = ({
  dagDisplayName,
  dagId,
  index,
  seed,
}: {
  dagDisplayName: string;
  dagId: string;
  index: number;
  seed: DagTaskSeed;
}): AirflowDagTask => ({
  task_id: seed.taskId,
  task_display_name: getTaskDisplayName(seed.taskId),
  owner: 'airflow',
  start_date: '2026-03-24T00:00:00Z',
  end_date: '2026-03-24T00:00:00Z',
  trigger_rule: seed.triggerRule,
  depends_on_past: false,
  wait_for_downstream: false,
  retries: 1,
  queue: 'default',
  pool: 'default_pool',
  pool_slots: 1,
  execution_timeout: { ...MOCK_TIME_DELTA },
  retry_delay: { ...MOCK_TIME_DELTA },
  retry_exponential_backoff: false,
  priority_weight: index + 1,
  weight_rule: 'downstream',
  ui_color: '#ffffff',
  ui_fgcolor: '#1f1f1f',
  template_fields: [],
  downstream_task_ids: [...seed.downstreamTaskIds],
  doc_md: `${dagDisplayName} - ${seed.taskId}`,
  operator_name: seed.operatorName,
  params: { dagId, taskId: seed.taskId },
  class_ref: { module_path: 'airflow.operators.mock' },
  is_mapped: false,
  extra_links: [],
});

const createDagRun = ({
  bundleVersion,
  dagDisplayName,
  dagId,
  duration,
  logicalDate,
  runId,
  runType,
  startDate,
  state,
  triggeredBy,
  triggeringUserName,
}: {
  bundleVersion: string;
  dagDisplayName: string;
  dagId: string;
  duration: number;
  logicalDate: string;
  runId: string;
  runType: string;
  startDate: string;
  state: AirflowDagRunState;
  triggeredBy: AirflowDagRunListItem['triggered_by'];
  triggeringUserName: string | null;
}): AirflowDagRunListItem => {
  const dagVersion = DAG_VERSION_FIXTURE(dagId, dagDisplayName, bundleVersion);
  const endDate = new Date(new Date(startDate).getTime() + duration * 1000).toISOString();

  return {
    dag_run_id: runId,
    dag_id: dagId,
    logical_date: logicalDate,
    queued_at: logicalDate,
    start_date: startDate,
    end_date: endDate,
    duration,
    data_interval_start: logicalDate,
    data_interval_end: endDate,
    run_after: logicalDate,
    last_scheduling_decision: startDate,
    run_type: runType,
    state,
    triggered_by: triggeredBy,
    triggering_user_name: triggeringUserName,
    conf: null,
    note: null,
    dag_versions: [{ ...dagVersion }],
    bundle_version: bundleVersion,
    dag_display_name: dagDisplayName,
  };
};

const createTaskInstance = ({
  dagDisplayName,
  dagId,
  logicalDate,
  run,
  state,
  task,
}: {
  dagDisplayName: string;
  dagId: string;
  logicalDate: string;
  run: AirflowDagRunListItem;
  state: AirflowTaskInstanceState;
  task: AirflowDagTask;
}): AirflowTaskInstance => ({
  id: `${run.dag_run_id}-${task.task_id}`,
  task_id: task.task_id,
  dag_id: dagId,
  dag_run_id: run.dag_run_id,
  map_index: -1,
  logical_date: logicalDate,
  run_after: run.run_after,
  start_date: run.start_date,
  end_date: run.end_date,
  duration: run.duration,
  state,
  try_number: 1,
  max_tries: task.retries,
  task_display_name: task.task_display_name,
  dag_display_name: dagDisplayName,
  hostname: 'worker-01',
  unixname: 'airflow',
  pool: task.pool,
  pool_slots: task.pool_slots,
  queue: task.queue,
  priority_weight: task.priority_weight,
  operator: task.operator_name,
  operator_name: task.operator_name,
  queued_when: run.queued_at,
  scheduled_when: run.logical_date,
  pid: 1234,
  executor: 'LocalExecutor',
  executor_config: '{}',
  note: null,
  rendered_map_index: null,
  rendered_fields: {},
  trigger: null,
  triggerer_job: null,
  dag_version: run.dag_versions[0] ?? null,
});

const createTaskSummary = ({
  run,
  state,
  task,
}: {
  run: AirflowDagRunListItem;
  state: AirflowTaskInstanceState;
  task: AirflowDagTask;
}): AirflowDagTaskInstanceSummary => ({
  task_id: task.task_id,
  state,
  child_states: state ? { [state]: 1 } : undefined,
  min_start_date: run.start_date ?? undefined,
  max_end_date: run.end_date ?? undefined,
});

const buildStructureResponse = (tasks: AirflowDagTask[]): AirflowDagStructureResponse => ({
  nodes: tasks.map((task) => ({
    id: task.task_id,
    label: task.task_display_name,
    operator: task.operator_name,
    type: 'task',
  })),
  edges: tasks
    .flatMap((task) =>
      task.downstream_task_ids.map((downstreamTaskId) => ({
        source_id: task.task_id,
        target_id: downstreamTaskId,
      }))
    )
    .map((edge) => ({
      ...edge,
      is_setup_teardown: 'false',
      is_source_asset: 'false',
    })),
});

const createDagDetailFixture = ({
  dag,
  bundleVersion,
  latestRunId,
  runDefinitions,
}: {
  dag: AirflowDagListItem;
  bundleVersion: string;
  latestRunId: string;
  runDefinitions: DagMockRunDefinition[];
}): DagMockDetailFixture => {
  const dagDisplayName = dag.dag_display_name ?? dag.dag_id;
  const taskSeeds = getTaskSeedsForDag(dag);
  const dagTasks = taskSeeds.map((seed, index) =>
    createDagTask({
      dagDisplayName,
      dagId: dag.dag_id,
      index,
      seed,
    })
  );
  const dagRuns = runDefinitions.map((runDefinition) =>
    createDagRun({
      bundleVersion,
      dagDisplayName,
      dagId: dag.dag_id,
      duration: runDefinition.duration,
      logicalDate: runDefinition.logicalDate,
      runId: runDefinition.runId,
      runType: runDefinition.runType,
      startDate: runDefinition.startDate,
      state: runDefinition.state,
      triggeredBy: runDefinition.triggeredBy,
      triggeringUserName: runDefinition.triggeringUserName,
    })
  );
  const latestRun = dagRuns.find((run) => run.dag_run_id === latestRunId) ?? dagRuns[0];
  const latestTaskInstances = dagTasks.reduce<Record<string, AirflowTaskInstance>>((acc, task) => {
    const taskState = runDefinitions[0]?.taskStates[task.task_id] ?? null;
    acc[task.task_id] = createTaskInstance({
      dagDisplayName,
      dagId: dag.dag_id,
      logicalDate: dagRuns[0]?.logical_date ?? dag.next_dagrun_logical_date ?? '2026-03-24T00:00:00Z',
      run: dagRuns[0]!,
      state: taskState,
      task,
    });
    return acc;
  }, {});
  const runStateMatrix = runDefinitions.reduce<Record<string, DagMockRunStateMatrix>>((acc, runDefinition) => {
    acc[runDefinition.runId] = { ...runDefinition.taskStates };
    return acc;
  }, {});

  return {
    dagDetail: createDagDetailResponse(dag, bundleVersion, dagTasks.length),
    dagCodeVersions: createDagCodeVersions(dag),
    dagRuns,
    dagTasks,
    latestTaskInstances,
    latestRun: {
      id: 1,
      dag_id: dag.dag_id,
      run_id: latestRun.dag_run_id,
      logical_date: latestRun.logical_date,
      run_after: latestRun.run_after,
      start_date: latestRun.start_date ?? undefined,
      end_date: latestRun.end_date ?? undefined,
      state: latestRun.state,
      duration: latestRun.duration ?? undefined,
    },
    runStateMatrix,
    structure: buildStructureResponse(dagTasks),
  };
};

const createInitialDagRunDefinitions = (dag: AirflowDagListItem): DagMockRunDefinition[] => {
  const latestRunAfter = dag.latest_dag_runs?.[0]?.run_after ?? dag.next_dagrun_logical_date ?? '2026-03-24T12:00:00Z';
  const latestStartDate = dag.latest_dag_runs?.[0]?.start_date ?? latestRunAfter;
  const latestState = dag.latest_dag_runs?.[0]?.state;
  const latestRunState =
    latestState === 'failed' || latestState === 'running' || latestState === 'queued' ? latestState : 'success';
  const taskSeeds = getTaskSeedsForDag(dag);

  const latestRunDefinitions: DagMockRunDefinition[] = [
    {
      runId: createRunId(dag.dag_id, INITIAL_DAG_RUN_COUNT),
      logicalDate: latestRunAfter,
      startDate: latestStartDate,
      duration: 480,
      state: latestRunState,
      runType: 'scheduled',
      triggeredBy: 'timetable',
      triggeringUserName: null,
      taskStates: createLatestRunTaskStates(latestState, taskSeeds),
    },
    {
      runId: createRunId(dag.dag_id, INITIAL_DAG_RUN_COUNT - 1),
      logicalDate: getShiftedIsoDate(latestRunAfter, 1),
      startDate: getShiftedIsoDate(latestStartDate, 1),
      duration: 420,
      state: 'success',
      runType: 'manual',
      triggeredBy: 'ui',
      triggeringUserName: 'opds-admin',
      taskStates: createSuccessTaskStates(taskSeeds),
    },
    {
      runId: createRunId(dag.dag_id, INITIAL_DAG_RUN_COUNT - 2),
      logicalDate: getShiftedIsoDate(latestRunAfter, 2),
      startDate: getShiftedIsoDate(latestStartDate, 2),
      duration: 390,
      state: 'success',
      runType: 'scheduled',
      triggeredBy: 'timetable',
      triggeringUserName: null,
      taskStates: createSuccessTaskStates(taskSeeds),
    },
    {
      runId: createRunId(dag.dag_id, INITIAL_DAG_RUN_COUNT - 3),
      logicalDate: getShiftedIsoDate(latestRunAfter, 3),
      startDate: getShiftedIsoDate(latestStartDate, 3),
      duration: 610,
      state: 'failed',
      runType: 'backfill',
      triggeredBy: 'backfill',
      triggeringUserName: 'scheduler',
      taskStates: createFailedTaskStates(taskSeeds),
    },
  ];
  const historicalRunPatterns = getHistoricalRunPatterns(taskSeeds);
  const historicalRunDefinitions = Array.from(
    { length: INITIAL_DAG_RUN_COUNT - latestRunDefinitions.length },
    (_, index) => {
      const pattern = historicalRunPatterns[index % historicalRunPatterns.length];
      const runNumber = INITIAL_DAG_RUN_COUNT - latestRunDefinitions.length - index;
      const dayOffset = index + latestRunDefinitions.length;

      return {
        runId: createRunId(dag.dag_id, runNumber),
        logicalDate: getShiftedIsoDate(latestRunAfter, dayOffset),
        startDate: getShiftedIsoDate(latestStartDate, dayOffset),
        duration: pattern.duration + (index % 3) * 15,
        state: pattern.state,
        runType: pattern.runType,
        triggeredBy: pattern.triggeredBy,
        triggeringUserName: pattern.triggeringUserName,
        taskStates: { ...pattern.taskStates },
      };
    }
  );

  return [...latestRunDefinitions, ...historicalRunDefinitions];
};

const createInitialDagDetailFixtures = (airflowDags: AirflowDagListItem[]): Record<string, DagMockDetailFixture> =>
  Object.fromEntries(
    airflowDags.map((dag, index) => {
      const bundleVersion = dag.version_snapshots?.[0]?.bundle_version ?? `v${index + 1}.0.0`;
      const runDefinitions = createInitialDagRunDefinitions(dag);

      return [
        dag.dag_id,
        createDagDetailFixture({
          dag,
          bundleVersion,
          latestRunId: createRunId(dag.dag_id, INITIAL_DAG_RUN_COUNT),
          runDefinitions,
        }),
      ];
    })
  ) as Record<string, DagMockDetailFixture>;

const INITIAL_DAG_DETAIL_FIXTURES = createInitialDagDetailFixtures(INITIAL_AIRFLOW_DAGS);

let airflowDagStore: AirflowDagListItem[] = INITIAL_AIRFLOW_DAGS.map(cloneDagListItem);
let airflowDagDetailFixtures: Record<string, DagMockDetailFixture> = structuredClone(INITIAL_DAG_DETAIL_FIXTURES);
let runtimeRunMetaByDagId = new Map<string, DagRuntimeRunMeta>();

const normalizeSearchValue = (value?: string) => value?.trim().toLowerCase() ?? '';

const findDagIndex = (dagId: string) => airflowDagStore.findIndex((dag) => dag.dag_id === dagId);
const findDashboardDagIndex = (dagUuid: string) => dashboardDagStore.findIndex((item) => item.dagUuid === dagUuid);

const normalizeDagId = (name: string) => {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '');

  return normalized || 'dag';
};

const createUniqueDagId = (name: string) => {
  const normalizedDagId = normalizeDagId(name);
  let nextDagId = normalizedDagId;
  let suffix = 2;

  while (findDagIndex(nextDagId) >= 0) {
    nextDagId = `${normalizedDagId}_${suffix}`;
    suffix += 1;
  }

  return nextDagId;
};

const createNewDagPlaceholderRun = ({
  dagDisplayName,
  dagId,
  bundleVersion,
  createdAt,
}: {
  dagDisplayName: string;
  dagId: string;
  bundleVersion: string;
  createdAt: string;
}): AirflowDagRunListItem => ({
  dag_run_id: `${dagId}-run-00`,
  dag_id: dagId,
  logical_date: createdAt,
  queued_at: null,
  start_date: null,
  end_date: null,
  duration: null,
  data_interval_start: null,
  data_interval_end: null,
  run_after: createdAt,
  last_scheduling_decision: null,
  run_type: 'manual',
  state: 'queued',
  triggered_by: null,
  triggering_user_name: null,
  conf: null,
  note: null,
  dag_versions: [DAG_VERSION_FIXTURE(dagId, dagDisplayName, bundleVersion)],
  bundle_version: bundleVersion,
  dag_display_name: dagDisplayName,
});

const createNewDagDetailFixture = ({
  dag,
  createdAt,
  datasourceId,
  maturity,
  tags,
}: {
  dag: AirflowDagListItem;
  createdAt: string;
  datasourceId: string;
  maturity: DagMaturity;
  tags: string[];
}): DagMockDetailFixture => {
  const dagDisplayName = dag.dag_display_name ?? dag.dag_id;
  const bundleVersion = 'v1.0.0';
  const taskSeeds = getTaskSeedsForDag(dag);
  const dagTasks = taskSeeds.map((seed, index) =>
    createDagTask({
      dagDisplayName,
      dagId: dag.dag_id,
      index,
      seed,
    })
  );
  const placeholderRun = createNewDagPlaceholderRun({
    dagDisplayName,
    dagId: dag.dag_id,
    bundleVersion,
    createdAt,
  });
  const latestTaskInstances = dagTasks.reduce<Record<string, AirflowTaskInstance>>((acc, task) => {
    acc[task.task_id] = createTaskInstance({
      dagDisplayName,
      dagId: dag.dag_id,
      logicalDate: createdAt,
      run: placeholderRun,
      state: null,
      task,
    });
    return acc;
  }, {});

  return {
    dagDetail: {
      ...createDagDetailResponse(dag, bundleVersion, dagTasks.length),
      bundle_name: `${dag.dag_id}-bundle`,
      bundle_version: bundleVersion,
      tags: dag.tags?.map((tag) => ({ ...tag })),
      owners: dag.owners ? [...dag.owners] : [],
      next_dagrun_logical_date: undefined,
      next_dagrun_data_interval_start: undefined,
      next_dagrun_data_interval_end: undefined,
      next_dagrun_run_after: undefined,
    },
    dagCodeVersions: createDagCodeVersions(dag, {
      dataSourceId: datasourceId,
      maturity,
      tags,
    }),
    dagRuns: [],
    dagTasks,
    latestTaskInstances,
    latestRun: {
      id: 0,
      dag_id: dag.dag_id,
      run_id: placeholderRun.dag_run_id,
    },
    runStateMatrix: {},
    structure: buildStructureResponse(dagTasks),
  };
};

const getDagFixture = (dagId: string): DagMockDetailFixture => {
  const fixture = airflowDagDetailFixtures[dagId];

  if (!fixture) {
    throw new Error(`Airflow DAG not found: ${dagId}`);
  }

  return fixture;
};

const shouldExposeDagRuntimeMock = (dagId: string, fixture: DagMockDetailFixture = getDagFixture(dagId)): boolean => {
  const dashboardItem = dashboardDagStore.find((item) => item.dagUuid === dagId);

  return dashboardItem?.hasDeployment === true || fixture.dagRuns.length > 0;
};

const getDagRuntimePhase = (startedAt: string, now: Date): DagRuntimePhase => {
  const elapsedMs = now.getTime() - Date.parse(startedAt);

  if (!Number.isFinite(elapsedMs) || elapsedMs < DAG_RUNTIME_PENDING_MS) {
    return 'pending';
  }

  if (elapsedMs < DAG_RUNTIME_SUCCESS_MS) {
    return 'running';
  }

  return 'success';
};

const getRuntimeRunState = (phase: DagRuntimePhase): AirflowDagRunState => {
  return phase === 'pending' ? 'queued' : phase;
};

const createRuntimeTaskStates = (
  tasks: AirflowDagTask[],
  phase: DagRuntimePhase,
  startedAt: string,
  now: Date
): DagMockRunStateMatrix => {
  if (phase === 'pending') {
    return tasks.reduce<DagMockRunStateMatrix>((acc, task) => {
      acc[task.task_id] = 'queued';
      return acc;
    }, {});
  }

  if (phase === 'success') {
    return createSuccessTaskStates(
      tasks.map((task) => ({
        downstreamTaskIds: [...task.downstream_task_ids],
        operatorName: task.operator_name,
        taskId: task.task_id,
        triggerRule: task.trigger_rule,
      }))
    );
  }

  const elapsedMs = Math.max(0, now.getTime() - Date.parse(startedAt));
  const runningWindowMs = DAG_RUNTIME_SUCCESS_MS - DAG_RUNTIME_PENDING_MS;
  const rawCurrentIndex = Math.floor(((elapsedMs - DAG_RUNTIME_PENDING_MS) / runningWindowMs) * tasks.length);
  const currentIndex = tasks.length <= 1 ? 0 : Math.min(tasks.length - 1, Math.max(1, rawCurrentIndex));

  return tasks.reduce<DagMockRunStateMatrix>((acc, task, index) => {
    if (index < currentIndex) {
      acc[task.task_id] = 'success';
    } else if (index === currentIndex) {
      acc[task.task_id] = 'running';
    } else {
      acc[task.task_id] = 'queued';
    }

    return acc;
  }, {});
};

const updateDagRuntimeDashboardState = ({
  dagUuid,
  lastRunAt,
  status,
  updatedAt,
  version,
}: {
  dagUuid: string;
  lastRunAt: string;
  status: DagListRunStatus;
  updatedAt: string;
  version: number;
}) => {
  const dashboardIndex = findDashboardDagIndex(dagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];

  if (dashboardIndex < 0 || !dashboardItem) {
    return;
  }

  dashboardDagStore[dashboardIndex] = {
    ...dashboardItem,
    updatedAt,
    versions: (dashboardItem.versions ?? []).map((item) =>
      item.version === version
        ? {
            ...item,
            status,
            lastRun: lastRunAt,
          }
        : { ...item }
    ),
  };
};

const updateAirflowDagRuntimeState = ({
  dagId,
  lastRunAt,
  runState,
  status,
}: {
  dagId: string;
  lastRunAt: string;
  runState: AirflowDagRunState;
  status: DagListRunStatus;
}) => {
  const dagIndex = findDagIndex(dagId);
  const dag = airflowDagStore[dagIndex];

  if (dagIndex < 0 || !dag) {
    return;
  }

  airflowDagStore[dagIndex] = {
    ...dag,
    latest_dag_runs: [
      {
        start_date: lastRunAt,
        logical_date: lastRunAt,
        run_after: lastRunAt,
        state: runState,
      },
    ],
    version_snapshots: dag.version_snapshots?.map((snapshot, index) =>
      index === 0
        ? {
            ...snapshot,
            run_status: status,
            last_run_at: lastRunAt,
            next_dagrun_run_after: lastRunAt,
            tags: [...snapshot.tags],
          }
        : { ...snapshot, tags: [...snapshot.tags] }
    ),
  };
};

const refreshDagRuntimeState = (dagUuid: string, now = new Date()): DagMockDetailFixture => {
  const fixture = getDagFixture(dagUuid);
  const runtimeMeta = runtimeRunMetaByDagId.get(dagUuid);

  if (!runtimeMeta) {
    return fixture;
  }

  const run = fixture.dagRuns.find((dagRun) => dagRun.dag_run_id === runtimeMeta.runId);

  if (!run) {
    runtimeRunMetaByDagId.delete(dagUuid);
    return fixture;
  }

  const phase = getDagRuntimePhase(runtimeMeta.startedAt, now);
  const runState = getRuntimeRunState(phase);
  const duration = Math.max(0, Math.floor((now.getTime() - Date.parse(runtimeMeta.startedAt)) / 1000));
  const nowIso = now.toISOString();
  const endDate = phase === 'success' ? nowIso : null;
  const taskStates = createRuntimeTaskStates(fixture.dagTasks, phase, runtimeMeta.startedAt, now);
  const updatedRun: AirflowDagRunListItem = {
    ...run,
    state: runState,
    duration,
    end_date: endDate,
    data_interval_end: endDate,
    last_scheduling_decision: nowIso,
  };
  const latestTaskInstances = fixture.dagTasks.reduce<Record<string, AirflowTaskInstance>>((acc, task) => {
    const state = taskStates[task.task_id] ?? null;
    const taskInstance = createTaskInstance({
      dagDisplayName: fixture.dagDetail.dag_display_name ?? dagUuid,
      dagId: dagUuid,
      logicalDate: runtimeMeta.startedAt,
      run: updatedRun,
      state,
      task,
    });

    acc[task.task_id] = {
      ...taskInstance,
      start_date: state && state !== 'queued' ? runtimeMeta.startedAt : null,
      end_date: state === 'success' ? nowIso : null,
      duration: state && state !== 'queued' ? duration : null,
    };
    return acc;
  }, {});
  const updatedFixture: DagMockDetailFixture = {
    ...fixture,
    dagRuns: fixture.dagRuns.map((dagRun) =>
      dagRun.dag_run_id === runtimeMeta.runId ? cloneDagRun(updatedRun) : cloneDagRun(dagRun)
    ),
    latestRun: {
      ...fixture.latestRun,
      dag_id: dagUuid,
      run_id: runtimeMeta.runId,
      logical_date: runtimeMeta.startedAt,
      run_after: runtimeMeta.startedAt,
      start_date: runtimeMeta.startedAt,
      end_date: endDate ?? undefined,
      state: runState,
      duration,
    },
    latestTaskInstances,
    runStateMatrix: {
      ...fixture.runStateMatrix,
      [runtimeMeta.runId]: { ...taskStates },
    },
  };

  airflowDagDetailFixtures[dagUuid] = updatedFixture;
  updateDagRuntimeDashboardState({
    dagUuid,
    lastRunAt: runtimeMeta.startedAt,
    status: phase,
    updatedAt: nowIso,
    version: runtimeMeta.version,
  });
  updateAirflowDagRuntimeState({
    dagId: dagUuid,
    lastRunAt: runtimeMeta.startedAt,
    runState,
    status: phase,
  });

  if (phase === 'success') {
    runtimeRunMetaByDagId.delete(dagUuid);
  }

  return updatedFixture;
};

const refreshAllDagRuntimeStates = () => {
  Array.from(runtimeRunMetaByDagId.keys()).forEach((dagUuid) => {
    if (!airflowDagDetailFixtures[dagUuid]) {
      runtimeRunMetaByDagId.delete(dagUuid);
      return;
    }

    refreshDagRuntimeState(dagUuid);
  });
};

const getSortedDagRuns = (dagRuns: AirflowDagRunListItem[], orderBy?: string[]): AirflowDagRunListItem[] => {
  const sortKey = orderBy?.[0] ?? '-run_after';

  if (sortKey === 'run_after') {
    return [...dagRuns].sort((left, right) => left.run_after.localeCompare(right.run_after));
  }

  return [...dagRuns].sort((left, right) => right.run_after.localeCompare(left.run_after));
};

const getNextDagRunId = (dagId: string, dagRuns: AirflowDagRunListItem[]): string => {
  const maxRunNumber = dagRuns.reduce((currentMax, dagRun) => {
    const match = dagRun.dag_run_id.match(/-run-(\d+)$/);

    if (!match) {
      return currentMax;
    }

    return Math.max(currentMax, Number(match[1]));
  }, 0);

  return `${dagId}-run-${String(maxRunNumber + 1).padStart(2, '0')}`;
};

export const resetAirflowDagMockStore = () => {
  const now = new Date();
  const initialAirflowDags = createInitialAirflowDags(now);
  const initialDashboardItems = createInitialDagDashboardItems(now);

  airflowDagStore = initialAirflowDags.map(cloneDagListItem);
  dashboardDagStore = initialDashboardItems.map(cloneDagDashboardItem);
  airflowDagDetailFixtures = structuredClone(createInitialDagDetailFixtures(initialAirflowDags));
  runtimeRunMetaByDagId = new Map<string, DagRuntimeRunMeta>();
};

export const createAirflowDagInMockStore = ({
  name,
  maturity,
  datasourceId,
  description,
  entityType,
  tags,
}: CreateAirflowDagInMockStoreParams): AirflowDagListItem => {
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const normalizedTags = tags.map((tag) => tag.trim()).filter(Boolean);

  if (!trimmedName) {
    throw new Error('Airflow DAG name is required');
  }

  const dagId = createUniqueDagId(trimmedName);
  const createdAt = new Date().toISOString();
  const nextDag: AirflowDagListItem = {
    dag_id: dagId,
    dag_display_name: trimmedName,
    is_paused: true,
    description: trimmedDescription,
    timetable_summary: '@once',
    owners: [],
    tags: normalizedTags.map((tag) => ({ name: tag })),
    has_import_errors: false,
    next_dagrun_logical_date: undefined,
    latest_dag_runs: [],
    version_snapshots: [
      createVersionSnapshot({
        bundleVersion: 'v1.0.0',
        description: trimmedDescription,
        timetableSummary: '@once',
        tags: [maturity, ...normalizedTags],
        runStatus: 'draft',
      }),
    ],
  };

  airflowDagStore = [nextDag, ...airflowDagStore];
  airflowDagDetailFixtures[dagId] = createNewDagDetailFixture({
    dag: nextDag,
    createdAt,
    datasourceId,
    maturity,
    tags: normalizedTags,
  });
  dashboardDagStore = [
    {
      dagUuid: dagId,
      name: trimmedName,
      description: trimmedDescription,
      maturity,
      entityType: entityType ?? null,
      dataSourceId: datasourceId || null,
      isSchedulingEnabled: false,
      hasDeployment: false,
      deployedVersion: null,
      latestVersion: 1,
      schedule: null,
      nextRun: null,
      versions: createDashboardVersions({
        version: 1,
        status: 'draft',
        lastRun: null,
      }),
      tags: normalizedTags,
      isBookmarked: false,
      createdAt,
      updatedAt: createdAt,
    },
    ...dashboardDagStore,
  ];

  return cloneDagListItem(nextDag);
};

export const getAirflowDagMockList = (params?: { dag_display_name_pattern?: string }): AirflowDagListResponse => {
  refreshAllDagRuntimeStates();

  const pattern = normalizeSearchValue(params?.dag_display_name_pattern);
  const dags = pattern
    ? airflowDagStore.filter((dag) => {
        const dagId = dag.dag_id.toLowerCase();
        const dagDisplayName = dag.dag_display_name?.toLowerCase() ?? '';

        return dagId.includes(pattern) || dagDisplayName.includes(pattern);
      })
    : airflowDagStore;

  return cloneDagListResponse(dags);
};

export const getDagDashboardListMock = (params?: DagDashboardListParams): DagDashboardListResponse => {
  refreshAllDagRuntimeStates();

  const normalizedKeyword = normalizeSearchValue(params?.keyword);
  const normalizedStatus = normalizeDashboardFilterValue(params?.status);
  const normalizedTag = normalizeDashboardFilterValue(params?.tag);
  const normalizedMaturity = normalizeDashboardFilterValue(params?.maturity);
  const sortDirection = params?.sortOrder === 'desc' ? -1 : 1;

  const filteredItems = dashboardDagStore.filter((item) => {
    const latestVersionItem = getLatestDashboardVersionItem(item);
    const latestStatus = normalizeDashboardFilterValue(latestVersionItem?.status ?? undefined);
    const itemName = item.name.toLowerCase();
    const itemDescription = item.description?.toLowerCase() ?? '';
    const matchesKeyword =
      !normalizedKeyword || itemName.includes(normalizedKeyword) || itemDescription.includes(normalizedKeyword);
    const matchesTag = !normalizedTag || (item.tags ?? []).some((tag) => tag.trim().toLowerCase() === normalizedTag);
    const matchesMaturity = !normalizedMaturity || item.maturity.trim().toLowerCase() === normalizedMaturity;
    const matchesStatus = !normalizedStatus || latestStatus === normalizedStatus;
    const matchesBookmark = params?.isBookmarked == null || item.isBookmarked === params.isBookmarked;

    return matchesKeyword && matchesStatus && matchesTag && matchesMaturity && matchesBookmark;
  });

  const sortedItems = [...filteredItems].sort((left, right) => {
    if (params?.sortBy === 'name') {
      return left.name.localeCompare(right.name) * sortDirection;
    }

    if (params?.sortBy === 'next_run') {
      return compareNullableDate(left.nextRun, right.nextRun) * sortDirection;
    }

    if (params?.sortBy === 'last_run') {
      return (
        compareNullableDate(
          getLatestDashboardVersionItem(left)?.lastRun,
          getLatestDashboardVersionItem(right)?.lastRun
        ) * sortDirection
      );
    }

    return 0;
  });

  const offset = params?.offset ?? 0;
  const slicedItems =
    typeof params?.limit === 'number' ? sortedItems.slice(offset, offset + params.limit) : sortedItems.slice(offset);

  return {
    items: slicedItems.map(cloneDagDashboardItem),
    total: sortedItems.length,
  };
};

export const getDagOverviewMock = (dagUuid: string): DagOverviewResponse => {
  const normalizedDagUuid = dagUuid.trim();
  refreshDagRuntimeState(normalizedDagUuid);

  const item = dashboardDagStore.find((dashboardItem) => dashboardItem.dagUuid === normalizedDagUuid);

  if (!item) {
    throw new Error(`DAG overview not found: ${dagUuid}`);
  }

  return mapDashboardItemToOverview(item);
};

export const getDagDashboardDetailMock = ({ dagUuid }: DagDashboardDetailRequest): DagDashboardResponse => {
  const normalizedDagUuid = dagUuid.trim();
  refreshDagRuntimeState(normalizedDagUuid);

  const item = dashboardDagStore.find((dashboardItem) => dashboardItem.dagUuid === normalizedDagUuid);

  if (!item) {
    throw new Error(`DAG dashboard detail not found: ${dagUuid}`);
  }

  return cloneDagDashboardItem(item);
};

export const getDagTaskGraphMock = (dagUuid: string): DagTaskGraphResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const fixture = refreshDagRuntimeState(normalizedDagUuid);

  if (!shouldExposeDagRuntimeMock(normalizedDagUuid, fixture)) {
    return { nodes: [], edges: [] };
  }

  return cloneDagTaskGraphResponse({
    nodes: fixture.dagTasks.map((task) => ({
      taskId: task.task_id,
      operator: task.operator_name,
      status: fixture.latestTaskInstances[task.task_id]?.state ?? null,
    })),
    edges: fixture.structure.edges.flatMap((edge) => {
      if (!edge.source_id || !edge.target_id) {
        return [];
      }

      return [
        {
          source: edge.source_id,
          target: edge.target_id,
        },
      ];
    }),
  });
};

export const getDagTasksMock = (dagUuid: string): DagTaskListResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const fixture = refreshDagRuntimeState(normalizedDagUuid);

  if (!shouldExposeDagRuntimeMock(normalizedDagUuid, fixture)) {
    return { tasks: [], total: 0 };
  }

  const tasks = fixture.dagTasks.map<DagTaskResponse>((task) => {
    const latestTaskInstance = fixture.latestTaskInstances[task.task_id] ?? null;

    return {
      taskId: task.task_id,
      operatorName: task.operator_name,
      function: task.task_id,
      triggerRule: task.trigger_rule,
      lastInstance: latestTaskInstance?.logical_date ?? null,
      lastStatus: latestTaskInstance?.state ?? null,
    };
  });

  return {
    tasks: tasks.map(cloneDagTaskResponse),
    total: tasks.length,
  };
};

export const getAirflowDagMockDetail = (dagId: string): AirflowDagDetailResponse => {
  return cloneDagDetail(refreshDagRuntimeState(dagId).dagDetail);
};

export const getAirflowDagCodeVersionsMock = (dagId: string): DagCodeVersion[] => {
  return refreshDagRuntimeState(dagId).dagCodeVersions.map(cloneDagCodeVersion);
};

export const getDagVersionListMock = ({ dagUuid }: DagVersionListRequest): DagVersionListResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const fixture = refreshDagRuntimeState(normalizedDagUuid);
  const dashboardItem = dashboardDagStore.find((item) => item.dagUuid === normalizedDagUuid);

  if (!dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const baseCreatedAt = dashboardItem.updatedAt ?? '2026-03-24T00:00:00Z';

  const items = fixture.dagCodeVersions
    .map<DagVersionResponse | null>((codeVersion, index) => {
      const [majorPart] = codeVersion.versionLabel.split('.');
      const version = Number(majorPart);

      if (!Number.isInteger(version) || version <= 0) {
        return null;
      }

      const versionItem = dashboardItem.versions?.find((item) => item.version === version);

      return {
        version,
        codeHash: `mock-${normalizedDagUuid}-${version}`,
        storagePath: `dags/${normalizedDagUuid}/versions/v${version}.py`,
        createdBy: 'mock-user',
        createdAt:
          version === dashboardItem.latestVersion
            ? baseCreatedAt
            : new Date(Date.parse(baseCreatedAt) - index * DAY_IN_MILLISECONDS).toISOString(),
        status: versionItem?.status ?? 'draft',
      };
    })
    .filter((item): item is DagVersionResponse => item !== null)
    .filter((item, index, items) => index === items.findIndex((candidate) => candidate.version === item.version))
    .sort((left, right) => right.version - left.version);

  return cloneDagVersionListResponse({
    items,
  });
};

export const getDagVersionCodeMock = ({ dagUuid, version }: DagVersionCodeRequest): DagVersionCodeResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const fixture = refreshDagRuntimeState(normalizedDagUuid);
  const dashboardItem = dashboardDagStore.find((item) => item.dagUuid === normalizedDagUuid);

  if (!dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const codeVersion = fixture.dagCodeVersions.find((item) => {
    const [majorPart] = item.versionLabel.split('.');
    return Number(majorPart) === version;
  });
  const versionItem = dashboardItem.versions?.find((item) => item.version === version);

  if (!codeVersion || !versionItem) {
    throw new Error(`DAG version code not found: ${dagUuid}/${version}`);
  }

  return cloneDagVersionCodeResponse({
    version,
    code: codeVersion.bodyCode,
    schedule: dashboardItem.deployedVersion === version ? (dashboardItem.schedule ?? undefined) : undefined,
    nextRun: dashboardItem.deployedVersion === version ? (dashboardItem.nextRun ?? undefined) : undefined,
    lastRun: versionItem.lastRun ?? undefined,
    status: versionItem.status ?? 'draft',
    installedLibraries: codeVersion.installedLibraries?.map((library) => ({ ...library })),
  });
};

export const getGlobalLibraryListMock = (): GlobalLibraryListResponse => {
  return cloneGlobalLibraryListResponse({
    items: DEFAULT_AVAILABLE_DAG_VERSION_LIBRARIES,
    total: DEFAULT_AVAILABLE_DAG_VERSION_LIBRARIES.length,
  });
};

export const getDagImmutableCodeMock = (): DagImmutableCodeResponse => {
  return {
    code: DAG_IMMUTABLE_HEADER_CODE,
  };
};

export const getDagTemplateCodeMock = ({ dagUuid }: DagTemplateCodeRequest): DagTemplateCodeResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const dashboardItem = dashboardDagStore.find((item) => item.dagUuid === normalizedDagUuid);

  if (!dashboardItem) {
    throw new Error(`DAG template not found: ${dagUuid}`);
  }

  return {
    maturity: dashboardItem.maturity,
    code: createDagTemplateBodyCode({
      dagId: normalizedDagUuid,
      name: dashboardItem.name,
      maturity: dashboardItem.maturity,
      dataSourceId: dashboardItem.dataSourceId,
      tags: dashboardItem.tags ?? [],
    }),
  };
};

export const uploadLibraryInMockStore = ({ filename }: UploadLibraryRequest): UploadLibraryResponse => {
  const normalizedFilename = filename.trim();
  const name = normalizedFilename.replace(/\.(py|whl|zip)$/i, '') || 'uploaded_library';

  return {
    name,
    message: `${name} uploaded to demo library store`,
  };
};

export const getDagTaskLogMock = ({ dagUuid, dagRunId, taskId }: DagTaskLogRequest): DagTaskLogResponse => {
  const fixture = getDagFixture(dagUuid.trim());
  const normalizedTaskId = taskId?.trim();
  const tasks = normalizedTaskId
    ? fixture.dagTasks.filter((task) => task.task_id === normalizedTaskId)
    : fixture.dagTasks;

  return {
    logs: tasks.map((task) => ({
      task_id: task.task_id,
      log: [
        `[${dagRunId}] 실행 ID 기준으로 ${task.task_display_name} 태스크를 시작합니다.`,
        `연산자: ${task.operator_name}`,
        `워크플로: ${fixture.dagDetail.dag_display_name ?? fixture.dagDetail.dag_id}`,
        `${task.task_id} 태스크가 완료되었습니다.`,
      ].join('\n'),
    })),
  };
};

type DagMockEventListener = (event: MessageEvent<string>) => void;

export const createDagTaskLogEventSourceMock = (request: DagTaskLogRequest): EventSource => {
  const response = getDagTaskLogMock(request);
  const listeners = new Map<string, Set<DagMockEventListener>>();
  let readyState = 0;
  let closed = false;

  const addListener = (eventName: string, listener: DagMockEventListener) => {
    const currentListeners = listeners.get(eventName) ?? new Set<DagMockEventListener>();
    currentListeners.add(listener);
    listeners.set(eventName, currentListeners);
  };

  const removeListener = (eventName: string, listener: DagMockEventListener) => {
    listeners.get(eventName)?.delete(listener);
  };

  const dispatchMockEvent = (eventName: string, data: string) => {
    const event = { type: eventName, data } as MessageEvent<string>;
    if (eventName === 'message') {
      eventSource.onmessage?.(event);
    }
    listeners.get(eventName)?.forEach((listener) => listener(event));
  };

  const eventSource = {
    get readyState() {
      return readyState;
    },
    url: 'mock://dag-task-log-stream',
    withCredentials: true,
    onopen: null,
    onmessage: null,
    onerror: null,
    close() {
      closed = true;
      readyState = 2;
    },
    addEventListener(eventName: string, listener: unknown) {
      if (typeof listener === 'function') {
        addListener(eventName, listener as DagMockEventListener);
      }
    },
    removeEventListener(eventName: string, listener: unknown) {
      if (typeof listener === 'function') {
        removeListener(eventName, listener as DagMockEventListener);
      }
    },
    dispatchEvent() {
      return true;
    },
  } as unknown as EventSource;

  globalThis.setTimeout(() => {
    if (closed) return;

    readyState = 1;
    eventSource.onopen?.({ type: 'open' } as Event);
    response.logs.forEach((entry) => {
      dispatchMockEvent('message', `[${entry.task_id}]`);
      entry.log
        .split('\n')
        .map((line) => line.replace(/\r$/, '').trimEnd())
        .filter((line) => Boolean(line))
        .forEach((line) => {
          dispatchMockEvent('message', line);
        });
    });
    dispatchMockEvent(
      'status',
      JSON.stringify({
        status: 'completed',
        message: '작업 로그 스트림이 종료되었습니다.',
      })
    );
  }, 0);

  return eventSource;
};

export const getDagExecutionChartMock = ({
  dagUuid,
  limit = 10,
  offset = 0,
}: {
  dagUuid: string;
  limit?: number;
  offset?: number;
}): DagExecutionChartResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const fixture = refreshDagRuntimeState(normalizedDagUuid);

  if (!shouldExposeDagRuntimeMock(normalizedDagUuid, fixture) || fixture.dagRuns.length === 0) {
    return {
      operators: [],
      runs: [],
      matrix: [],
      total: 0,
      limit,
      offset,
    };
  }

  const tasks = fixture.dagTasks.map(cloneDagTask);
  const allRuns = getSortedDagRuns(fixture.dagRuns);
  const slicedRuns = allRuns.slice(offset, offset + limit);

  return {
    operators: tasks.map((task) => task.task_id),
    runs: slicedRuns.map((run) => ({
      runId: run.dag_run_id,
      logicalDate: run.logical_date,
      duration: run.duration ?? null,
      state: normalizeDagTaskStatus(run.state),
    })),
    matrix: tasks.map((task) =>
      slicedRuns.map((run) => fixture.runStateMatrix[run.dag_run_id]?.[task.task_id] ?? 'none')
    ),
    total: allRuns.length,
    limit,
    offset,
  };
};

export const saveDagVersionInMockStore = ({
  dagUuid,
  code,
  installedLibraries = [],
}: {
  dagUuid: string;
  code: string;
  installedLibraries?: DagInstalledLibrary[];
}): SaveDagVersionResponse => {
  const dashboardIndex = findDashboardDagIndex(dagUuid);
  const fixture = getDagFixture(dagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];

  if (dashboardIndex < 0 || !dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const currentMaxVersion = Math.max(
    dashboardItem.latestVersion ?? 0,
    ...(dashboardItem.versions?.map((item) => item.version) ?? [0])
  );
  const nextVersion = currentMaxVersion + 1;
  const createdAt = new Date().toISOString();

  dashboardDagStore[dashboardIndex] = {
    ...dashboardItem,
    latestVersion: nextVersion,
    updatedAt: createdAt,
    versions: [
      {
        version: nextVersion,
        status: 'draft',
        lastRun: undefined,
      },
      ...(dashboardItem.versions?.map((item) => ({ ...item })) ?? []),
    ],
  };

  airflowDagDetailFixtures[dagUuid] = {
    ...fixture,
    dagCodeVersions: [
      {
        versionLabel: `${nextVersion}.0`,
        bodyCode: code,
        importedLibraries: installedLibraries.map((library) => ({ module: library.name })),
        installedLibraries: installedLibraries.map((library) => ({ ...library })),
      },
      ...fixture.dagCodeVersions.map(cloneDagCodeVersion),
    ],
  };

  return cloneSaveDagVersionResponse({
    version: nextVersion,
    codeHash: `mock-${dagUuid}-${nextVersion}`,
    storagePath: `s3://mock-dags/${dagUuid}/versions/${nextVersion}.py`,
    createdBy: 'mock-user',
    createdAt,
    status: 'draft',
  });
};

export const updateDagInMockStore = ({ dagUuid, description, tags }: UpdateDagRequest): UpdateDagResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const dashboardIndex = findDashboardDagIndex(normalizedDagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];

  if (dashboardIndex < 0 || !dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const updatedItem: DagDashboardResponse = {
    ...dashboardItem,
    description: description === undefined ? dashboardItem.description : description,
    tags: tags === undefined ? dashboardItem.tags : (tags ?? []),
    updatedAt: new Date().toISOString(),
  };

  dashboardDagStore[dashboardIndex] = updatedItem;

  return cloneDagDashboardItem(updatedItem);
};

export const toggleDagBookmarkInMockStore = (dagUuid: string): ToggleDagBookmarkResponse => {
  const normalizedDagUuid = dagUuid.trim();
  const dashboardIndex = findDashboardDagIndex(normalizedDagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];

  if (dashboardIndex < 0 || !dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const isBookmarked = !(dashboardItem.isBookmarked ?? false);

  dashboardDagStore[dashboardIndex] = {
    ...dashboardItem,
    isBookmarked,
    updatedAt: new Date().toISOString(),
  };

  return { isBookmarked };
};

export const deployDagInMockStore = ({ dagUuid, version }: { dagUuid: string; version: number }): DeployDagResponse => {
  const dashboardIndex = findDashboardDagIndex(dagUuid);
  const fixture = getDagFixture(dagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];
  const deployedAt = new Date().toISOString();

  if (dashboardIndex < 0 || !dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const hasVersion = (dashboardItem.versions ?? []).some((item) => item.version === version);

  if (!hasVersion) {
    throw new Error(`DAG version not found: ${dagUuid}/${version}`);
  }

  dashboardDagStore[dashboardIndex] = {
    ...dashboardItem,
    hasDeployment: true,
    deployedVersion: version,
    updatedAt: deployedAt,
    versions: (dashboardItem.versions ?? []).map((item) =>
      item.version === version
        ? {
            ...item,
            status: 'success',
          }
        : { ...item }
    ),
  };

  airflowDagDetailFixtures[dagUuid] = {
    ...fixture,
    dagDetail: {
      ...fixture.dagDetail,
      bundle_version: versionLabelToBundleVersion(`${version}.0`),
    },
  };

  const dagIndex = findDagIndex(dagUuid);
  const airflowDag = airflowDagStore[dagIndex];

  if (dagIndex >= 0 && airflowDag) {
    airflowDagStore[dagIndex] = {
      ...airflowDag,
      version_snapshots: airflowDag.version_snapshots?.map((snapshot, index) =>
        index === 0
          ? {
              ...snapshot,
              run_status: 'success',
              tags: [...snapshot.tags],
            }
          : { ...snapshot, tags: [...snapshot.tags] }
      ),
    };
  }

  return cloneDeployDagResponse({
    deployedVersion: version,
  });
};

export const toggleDagSchedulingInMockStore = ({ dagUuid, enabled }: ToggleDagSchedulingRequest): void => {
  const normalizedDagUuid = dagUuid.trim();
  const dashboardIndex = findDashboardDagIndex(normalizedDagUuid);
  const dashboardItem = dashboardDagStore[dashboardIndex];
  const fixture = getDagFixture(normalizedDagUuid);

  if (dashboardIndex < 0 || !dashboardItem) {
    throw new Error(`DAG not found: ${dagUuid}`);
  }

  const schedule = enabled ? (dashboardItem.schedule ?? '@daily') : null;
  const nextRun = enabled ? (dashboardItem.nextRun ?? new Date(Date.now() + DAY_IN_MILLISECONDS).toISOString()) : null;

  dashboardDagStore[dashboardIndex] = {
    ...dashboardItem,
    isSchedulingEnabled: enabled,
    schedule,
    nextRun,
    updatedAt: new Date().toISOString(),
  };

  airflowDagDetailFixtures[normalizedDagUuid] = {
    ...fixture,
    dagDetail: {
      ...fixture.dagDetail,
      is_paused: !enabled,
      timetable_summary: schedule ?? undefined,
      next_dagrun_logical_date: nextRun ?? undefined,
    },
  };

  const airflowDagIndex = findDagIndex(normalizedDagUuid);

  if (airflowDagIndex >= 0) {
    airflowDagStore[airflowDagIndex] = {
      ...airflowDagStore[airflowDagIndex],
      is_paused: !enabled,
      timetable_summary: schedule ?? undefined,
      next_dagrun_logical_date: nextRun ?? undefined,
    };
  }
};

/** latestRun.state는 Airflow(`failed` 등) 값이며, UI 배지는 `normalizeDagListRunStatus`로 목록과 동일하게 매핑합니다. */
export const getAirflowLatestDagRunMock = (dagId: string): AirflowLatestRunResponse => {
  return { ...refreshDagRuntimeState(dagId).latestRun };
};

export const getAirflowDagTasksMock = (dagId: string): AirflowDagTasksResponse => {
  const tasks = refreshDagRuntimeState(dagId).dagTasks.map(cloneDagTask);

  return {
    tasks,
    total_entries: tasks.length,
  };
};

export const getAirflowDagStructureMock = (dagId: string): AirflowDagStructureResponse => {
  const structure = refreshDagRuntimeState(dagId).structure;

  return {
    nodes: structure.nodes.map((node) => ({ ...node, children: node.children?.map((child) => ({ ...child })) })),
    edges: structure.edges.map((edge) => ({ ...edge })),
  };
};

export const getAirflowDagGridRunsMock = ({
  dagId,
  limit,
}: {
  dagId: string;
  limit?: number;
}): AirflowDagGridRunsResponse => {
  const sortedRuns = getSortedDagRuns(refreshDagRuntimeState(dagId).dagRuns);
  const slicedRuns = typeof limit === 'number' ? sortedRuns.slice(0, limit) : sortedRuns;

  return slicedRuns.map((run) =>
    cloneGridRun({
      dag_id: run.dag_id,
      duration: run.duration ?? undefined,
      end_date: run.end_date ?? undefined,
      queued_at: run.queued_at ?? undefined,
      run_after: run.run_after,
      run_id: run.dag_run_id,
      run_type: run.run_type,
      start_date: run.start_date ?? undefined,
      state: run.state,
    })
  );
};

export const getAirflowDagRunListMock = ({
  dagId,
  limit,
  offset = 0,
  orderBy,
}: {
  dagId: string;
  limit?: number;
  offset?: number;
  orderBy?: string[];
}): AirflowDagRunListResponse => {
  const fixture = refreshDagRuntimeState(dagId);

  if (!shouldExposeDagRuntimeMock(dagId, fixture)) {
    return {
      dag_runs: [],
      total_entries: 0,
    };
  }

  const sortedRuns = getSortedDagRuns(fixture.dagRuns, orderBy);
  const offsetRuns = sortedRuns.slice(offset);
  const dagRuns = typeof limit === 'number' ? offsetRuns.slice(0, limit) : offsetRuns;

  return {
    dag_runs: dagRuns.map(cloneDagRun),
    total_entries: sortedRuns.length,
  };
};

export const getAirflowDagTaskInstanceSummaryMock = ({
  dagId,
  runId,
}: {
  dagId: string;
  runId: string;
}): AirflowDagTaskInstanceSummaryResponse => {
  const fixture = refreshDagRuntimeState(dagId);
  const taskStates = fixture.runStateMatrix[runId];
  const run = fixture.dagRuns.find((dagRun) => dagRun.dag_run_id === runId);

  if (!taskStates || !run) {
    throw new Error(`Airflow DAG run not found: ${dagId}/${runId}`);
  }

  return {
    run_id: runId,
    dag_id: dagId,
    task_instances: fixture.dagTasks.map((task) =>
      cloneTaskSummary(
        createTaskSummary({
          run,
          state: taskStates[task.task_id] ?? null,
          task,
        })
      )
    ),
  };
};

export const getAirflowTaskInstancesMock = ({
  dagId,
  taskId,
}: {
  dagId: string;
  taskId: string;
}): AirflowTaskInstancesResponse => {
  const fixture = refreshDagRuntimeState(dagId);
  const taskInstance = fixture.latestTaskInstances[taskId];

  if (!taskInstance) {
    throw new Error(`Airflow task not found: ${dagId}/${taskId}`);
  }

  return {
    task_instances: [cloneTaskInstance(taskInstance)],
    total_entries: 1,
  };
};

export const getDagExecutionTaskInstancesMock = ({
  dagId,
  runId,
}: {
  dagId: string;
  runId: string;
}): DagExecutionTaskInstance[] => {
  const fixture = refreshDagRuntimeState(dagId);
  const run = fixture.dagRuns.find((dagRun) => dagRun.dag_run_id === runId);
  const taskStates = fixture.runStateMatrix[runId];

  if (!run || !taskStates) {
    return [];
  }

  return fixture.dagTasks.map((task) => {
    const state = taskStates[task.task_id] ?? null;
    const isStarted = Boolean(state && state !== 'queued');
    const isFinished = state === 'success' || state === 'failed' || state === 'upstream_failed';

    return {
      taskId: task.task_id,
      state,
      startDate: isStarted ? (run.start_date ?? undefined) : undefined,
      endDate: isFinished ? (run.end_date ?? undefined) : undefined,
      duration: isStarted ? (run.duration ?? undefined) : undefined,
      operator: task.task_id,
      tryNumber: 1,
      triggerRule: task.trigger_rule,
    };
  });
};

export const updateAirflowDagPauseStateInMockStore = (
  dagId: string,
  payload: AirflowDagPatchBody
): AirflowDagDetailResponse => {
  const dagIndex = findDagIndex(dagId);
  const fixture = getDagFixture(dagId);

  if (dagIndex < 0) {
    throw new Error(`Airflow DAG not found: ${dagId}`);
  }

  airflowDagStore[dagIndex] = {
    ...airflowDagStore[dagIndex],
    is_paused: payload.is_paused,
  };
  airflowDagDetailFixtures[dagId] = {
    ...fixture,
    dagDetail: {
      ...fixture.dagDetail,
      is_paused: payload.is_paused,
    },
  };

  return cloneDagDetail(airflowDagDetailFixtures[dagId].dagDetail);
};

export const triggerAirflowDagInMockStore = (dagId: string, version?: number | null): AirflowDagRunListItem => {
  const dagIndex = findDagIndex(dagId);
  const fixture = getDagFixture(dagId);

  if (dagIndex < 0) {
    throw new Error(`Airflow DAG not found: ${dagId}`);
  }

  const dashboardIndex = findDashboardDagIndex(dagId);
  const dashboardItem = dashboardDagStore[dashboardIndex];
  const targetVersion =
    typeof version === 'number' && Number.isFinite(version)
      ? version
      : (dashboardItem?.deployedVersion ?? dashboardItem?.latestVersion ?? 1);
  const triggeredAt = new Date().toISOString();
  const runId = getNextDagRunId(dagId, fixture.dagRuns);
  const taskStates = fixture.dagTasks.reduce<Record<string, AirflowTaskInstanceState>>((acc, task) => {
    acc[task.task_id] = 'queued';
    return acc;
  }, {});
  const newDagRun = createDagRun({
    bundleVersion: fixture.dagDetail.bundle_version ?? 'v1.0.0',
    dagDisplayName: fixture.dagDetail.dag_display_name ?? dagId,
    dagId,
    duration: 0,
    logicalDate: triggeredAt,
    runId,
    runType: 'manual',
    startDate: triggeredAt,
    state: 'queued',
    triggeredBy: 'ui',
    triggeringUserName: 'opds-admin',
  });
  const queuedDagRun: AirflowDagRunListItem = {
    ...newDagRun,
    end_date: null,
    duration: 0,
    data_interval_end: null,
  };

  const latestTaskInstances = fixture.dagTasks.reduce<Record<string, AirflowTaskInstance>>((acc, task) => {
    acc[task.task_id] = createTaskInstance({
      dagDisplayName: fixture.dagDetail.dag_display_name ?? dagId,
      dagId,
      logicalDate: triggeredAt,
      run: queuedDagRun,
      state: 'queued',
      task,
    });
    return acc;
  }, {});

  airflowDagDetailFixtures[dagId] = {
    ...fixture,
    dagRuns: [queuedDagRun, ...fixture.dagRuns.map(cloneDagRun)],
    latestRun: {
      id: fixture.latestRun.id + 1,
      dag_id: dagId,
      run_id: runId,
      logical_date: triggeredAt,
      run_after: triggeredAt,
      start_date: triggeredAt,
      end_date: undefined,
      state: 'queued',
      duration: 0,
    },
    latestTaskInstances,
    runStateMatrix: {
      [runId]: taskStates,
      ...fixture.runStateMatrix,
    },
  };

  if (dashboardIndex >= 0 && dashboardItem) {
    dashboardDagStore[dashboardIndex] = {
      ...dashboardItem,
      updatedAt: triggeredAt,
      versions: (dashboardItem.versions ?? []).map((item) =>
        item.version === targetVersion
          ? {
              ...item,
              status: 'pending',
              lastRun: triggeredAt,
            }
          : { ...item }
      ),
    };
  }

  airflowDagStore[dagIndex] = {
    ...airflowDagStore[dagIndex],
    latest_dag_runs: [
      {
        start_date: triggeredAt,
        logical_date: triggeredAt,
        run_after: triggeredAt,
        state: 'queued',
      },
    ],
    version_snapshots: airflowDagStore[dagIndex].version_snapshots?.map((snapshot, index) =>
      index === 0
        ? {
            ...snapshot,
            run_status: 'pending',
            last_run_at: triggeredAt,
            next_dagrun_run_after: triggeredAt,
          }
        : { ...snapshot, tags: [...snapshot.tags] }
    ),
  };
  runtimeRunMetaByDagId.set(dagId, {
    runId,
    startedAt: triggeredAt,
    version: targetVersion,
  });

  return cloneDagRun(queuedDagRun);
};

export const triggerDagInMockStore = ({
  dagUuid,
  version,
  deployed: _deployed,
  code: _code,
}: TriggerDagRequest): TriggerDagResponse => {
  const run = triggerAirflowDagInMockStore(dagUuid, typeof version === 'number' ? version : null);

  return {
    runId: run.dag_run_id,
    status: run.state ?? 'pending',
  };
};

export const deleteAirflowDagInMockStore = (dagId: string): void => {
  const dagIndex = findDagIndex(dagId);
  const dashboardIndex = findDashboardDagIndex(dagId);

  if (dagIndex < 0) {
    throw new Error(`Airflow DAG not found: ${dagId}`);
  }

  airflowDagStore.splice(dagIndex, 1);
  if (dashboardIndex >= 0) {
    dashboardDagStore.splice(dashboardIndex, 1);
  }
  delete airflowDagDetailFixtures[dagId];
  runtimeRunMetaByDagId.delete(dagId);
};
